// ============================================================
// vehicle.js
// EV Powertrain Simulator — core 모듈
// 차량 물리 — 도로부하, 구동계, 기어, 차량 상태 적분기
// Extracted from ev-sim_v1_0_Verification_46.jsx (Phase 2)
// ============================================================

// [Fix-4 Hotfix] physics_consts.js import 제거 → 인라인 상수 (Vite 번들 크래시 방지)
// ★ [Fix-C1] 값을 9.80665(SI 표준)로 통일. physics_consts.js G_GRAVITY와 반드시 동기화 유지.

// ── [Phase4 Fix] vehicle.js 누락 함수 보완 ──
// [rhoCorrected-fix] math_utils에서 import — vehicle.js 내부 사용 + re-export용
import { rhoCorrected } from './math_utils.js';

// [Fix-Issue1] motor.js 의존 함수 명시적 import — 공유 스코프 암묵 의존 제거
import { motorTorqueSpeed, getLUTPkScale, computeMotorLosses, computeInverterLosses, I_max_T, T_max_motor_T } from './motor.js'; // [Sprint-2]

// [Fix-Issue1] drive_control.js 의존 함수 명시적 import
import { regenTorqueEnhanced } from './drive_control.js';

// [Fix-Issue1] battery.js 의존 함수 명시적 import
import { P_dis_max_T, computeBatteryLosses } from './battery.js'; // [Sprint-2]

// [Fix-4 Hotfix 해제] physics_consts.js import 복구 — zero-import 제약 없는 파일이므로
// SST 원본을 직접 참조. 인라인 상수 이중 관리 위험 제거.
import { G_GRAVITY } from '../../constants/physics_consts.js';
import { hvacDetailedPower } from './thermal.js'; // [Sprint-2] fix: was implicit global
const g = G_GRAVITY; // 하위 호환 별칭 (내부 코드에서 g 사용처 다수)


// ── CrrDynamic ──
/**
 * 속도와 타이어 온도에 의존하는 동적 구름저항 계수를 계산한다.
 *
 * Crr(v,T) = Crr_base × (1 + Crr_v_coeff × v²) × T_factor
 *   · v² 의존: 고속에서 타이어 히스테리시스 증가 반영
 *   · 저온(T_tire < 20°C): 고무 점도 증가 → Crr 상승
 *     T_factor = 1 + Crr_temp_coeff × (20 − T_tire)
 *
 * @param {number} v        차속 [m/s]
 * @param {number} T_tire   타이어 온도 [°C]
 * @param {object} params   { Crr, Crr_v_coeff, Crr_temp_coeff }
 * @returns {number}        동적 구름저항 계수 [무차원]
 */
function CrrDynamic(v, T_tire, params) {
  const v_factor = 1 + params.Crr_v_coeff * v * v;
  const T_factor = T_tire < 20 ? 1 + params.Crr_temp_coeff * (20 - T_tire) : 1.0;
  return params.Crr * v_factor * T_factor;
}

// ── CrrWithPressure ──
/**
 * 타이어 온도 + 공기압 편차를 모두 반영한 구름저항 계수를 계산한다.
 *
 * Crr_total = CrrDynamic(v, T_tire) + ΔCrr_pressure
 *   ΔCrr_pressure = max(0, alpha_p × dp + beta_p × dp²)
 *   dp = (p_nom − p_act) / p_nom  (정규화 압력 편차, 저압 시 양수)
 *
 * [Fix-WARN-3] 이 함수가 온도·공기압 Crr 보정의 단일 정의(single source of truth).
 *   resistanceForcesV3는 더 이상 동일 로직을 인라인 중복하지 않고 이 함수에 위임.
 *   외부 API로도 노출되므로 삭제하지 않되, 파라미터(alpha_p/beta_p 등) 수정 시
 *   이 구현만 변경하면 resistanceForcesV3에도 자동 반영됨.
 */
function CrrWithPressure(v, T_tire, params) {
  // [Fix-⑥] typeof CrrDynamic === 'function' 가드 제거:
  //   동일 파일 내 정의이므로 항상 존재. typeof 체크는 불필요한 방어코드였음.
  const Crr_base = CrrDynamic(v, T_tire, params);
  // [Fix-CrrP-01] 이중 경로 통일: resistanceForcesV3와 동일한 alpha_p/beta_p 가산식으로 통일
  //   수정 전: multiplicative factor  → Crr_base * (1 + k_p * (p_nom-p_act)/p_nom)
  //   수정 후: additive delta Crr    → Crr_base + alpha_p*dp + beta_p*dp²
  //   (dp = (p_nom-p_act)/p_nom, 무차원 정규화)
  const p_nom   = params.tire_pressure_nominal || 2.4;
  const p_act   = params.tire_pressure_actual  || 2.4;
  const dp      = (p_nom - p_act) / p_nom;            // 정규화 압력 편차
  const alpha_p = params.tire_pressure_crr_alpha || 0.008;
  const beta_p  = params.tire_pressure_crr_beta  || 0.0003;
  const delta_Crr_p = Math.max(0, alpha_p * dp + beta_p * dp * dp);
  return Crr_base + delta_Crr_p;
}

// ── resistanceForcesV3 ──
/**
 * 주행 저항력 전체를 계산한다 (공기·구름·경사·노면거칠기·측풍 포함).
 *
 * 반환 항목:
 *   F_aero   = ½·ρ·CdA_eff·v_eff²  (가변 CdA + 측풍 효과 포함)
 *   F_roll   = m·g·Crr(v,T)·cos(θ) (온도·압력 보정 Crr)
 *   F_grade  = m·g·sin(θ)
 *   F_rough  = ΔCrr_IRI·m·g         (IRI 노면 거칠기 추가 저항)
 *   F_down / dFz_aero_f/r: ClA 기반 다운포스 → computeVerticalLoad_v4 입력용
 *
 * 보정 계수: K_aero·K_roll·K_grade (파라미터 튜닝용)
 *
 * @param {number} v         차속 [m/s]
 * @param {object} params    차량 파라미터
 * @param {number} grade_rad 경사각 [rad]
 * @param {number} T_amb     외기온 [°C] (공기밀도 보정용)
 * @param {number} T_tire    타이어 온도 [°C] (Crr 온도 보정용)
 * @param {number} T_motor   모터 온도 [°C] (가변 CdA 그릴 셔터 제어용)
 * @returns {{ F_aero, F_roll, F_grade, F_rough, F_total, F_down, dFz_aero_f, dFz_aero_r, rho_eff, Crr_eff, ... }}
 */
function resistanceForcesV3(v, params, grade_rad, T_amb, T_tire, T_motor) {
  // [R-19-inline-C] crosswindAeroForce 내부 함수 (글로벌 스코프에서 이동)
  function crosswindAeroForce(v_vehicle, params, rho_eff) {
    if (!params.crosswind_enable || !params.v_wind) {
      // No crosswind: standard aero
      return { F_aero_cw: 0.5 * rho_eff * params.CdA * v_vehicle * v_vehicle, v_eff: v_vehicle, v_wind: 0 };
    }
    const beta = (params.wind_angle_deg || 90) * Math.PI / 180;
    const v_w = params.v_wind || 0;
    // Effective headwind component (longitudinal)
    const v_long = v_vehicle + v_w * Math.cos(beta);
    // Lateral wind component → lateral drag (adds to effective drag via induced yaw)
    const v_lat = v_w * Math.sin(beta);
    // Effective velocity magnitude for drag calculation
    const v_eff = Math.sqrt(v_long * v_long + (params.crosswind_lat_factor || 0.3) * v_lat * v_lat); // [Fix-⑤] 측풍 횡방향 기여계수 파라미터화 (기본 0.3)
    const CdA_eff = params.CdA_v3 || params.CdA;
    const F_aero_cw = 0.5 * rho_eff * CdA_eff * v_eff * v_eff;
    const F_aero_no_cw = 0.5 * rho_eff * CdA_eff * v_vehicle * v_vehicle;
    return {
      F_aero_cw,
      F_aero_no_cw,
      delta_F_wind: F_aero_cw - F_aero_no_cw, // crosswind additional drag
      v_eff,
      v_long,
      v_lat,
      v_wind: v_w,
      beta_deg: params.wind_angle_deg
    };
  }

  const rho  = (T_amb   !== undefined) ? rhoCorrected(T_amb, params.altitude || 0) : params.rho;
  const Ka   = params.K_aero  || 1.0;
  const Kr   = params.K_roll  || 1.0;
  const Kg   = params.K_grade || 1.0;

  // ── 기본 Crr (온도 의존) + 공기압 보정 ──────────────────────────────
  // [Fix-WARN-3] 공기압 보정 중복 제거 — CrrWithPressure를 단일 정의(single source of truth)로 사용
  //   수정 전: alpha_p/beta_p 가산식이 CrrWithPressure와 이 함수에 각각 독립적으로 존재
  //            → 파라미터 수정 시 한쪽만 반영될 위험 (실제 Fix-CrrP-01 당시 두 곳 동시 수정 필요했음)
  //   수정 후: 온도·공기압 보정을 모두 포함한 CrrWithPressure에 위임
  //            resistanceForcesV3는 T_tire가 없을 때만 params.Crr 폴백 유지
  const Crr = (T_tire !== undefined) ? CrrWithPressure(v, T_tire, params) : params.Crr;
  // 진단 반환용: 공기압 보정 기여분만 분리 (CrrWithPressure 내부 로직과 동일한 공식)
  const _p_nom_d = params.tire_pressure_nominal || 2.4;
  const _p_act_d = params.tire_pressure_actual  || 2.4;
  const _dp_d    = (_p_nom_d - _p_act_d) / _p_nom_d;
  const delta_Crr_pressure = Math.max(0,
    (params.tire_pressure_crr_alpha || 0.008) * _dp_d +
    (params.tire_pressure_crr_beta  || 0.0003) * _dp_d * _dp_d);

  // ── IRI 노면 거칠기 추가 저항 (구 roadRoughnessResistance 통합) ──────
  let F_rough = 0, delta_Crr_IRI = 0;
  if (params.road_roughness_enable) {
    const IRI       = params.IRI || 2.5;
    delta_Crr_IRI   = (params.k_IRI_crr || 0.00015) * IRI
                    + (params.k_IRI_vib  || 0.000008) * IRI * v;
    F_rough         = Math.max(0, delta_Crr_IRI * params.m * g);
  }

  // ── 가변 CdA + 측풍 공기력 ─────────────────────────────────────────
  const cdaResult = variableCdA(v, params, T_motor);
  // [Fix-Mutate] params.CdA_v3 직접 변이(mutate) 제거
  //   기존: params.CdA_v3 = cdaResult.CdA_v3 → params 객체 부작용, 반복 호출 시 값 오염 가능
  //   수정: 반환값 CdA_v3 필드로만 노출. 호출자가 필요 시 result.CdA_v3를 직접 읽을 것.
  const cwResult  = crosswindAeroForce(v, params, rho);
  const F_aero    = cwResult.F_aero_cw * Ka;
  const F_roll    = params.m * g * Crr * Math.cos(grade_rad) * Kr;
  const F_grade   = params.m * g * Math.sin(grade_rad) * Kg;

  // ── [Low-Lift] 공기역학적 양력/다운포스 — Fz_aero ──────────────────
  // 고속 주행 시 차체에 작용하는 수직 공기력: 양력(+) 또는 다운포스(-)
  //   F_lift = ½·ρ·Cl·A_ref·v²
  //   Cl > 0: 양력 (승용차 일반) → Fz 감소 → 그립 감소
  //   Cl < 0: 다운포스 (스포츠카/능동 스포일러) → Fz 증가 → 그립 증가
  //
  // 그립 영향:
  //   Fz_static = m·g / 4  →  Fz_aero_per_corner = F_lift / 4
  //   타이어 종/횡 그립은 Fz에 비례하므로 Fz_aero가 작용하면 tire Pacejka Fz 보정 필요
  //
  // 파라미터:
  //   params.Cl_body        : 차체 양력계수 (기본 0.10 — 일반 세단 근사)
  //   params.A_ref          : 정면 투영 면적 [m²] (기본 CdA / Cd ≈ 2.2 m²)
  //   params.aero_lift_enable: false면 기존 동작 유지 (기본 false — opt-in)
  //
  // 참고: Barnard (2001) "Road Vehicle Aerodynamic Design" §6; SAE 870242
  let F_lift = 0;
  if (params.aero_lift_enable) {
    const Cl    = params.Cl_body    ?? 0.10;   // 양력계수 (양수=양력, 음수=다운포스)
    const A_ref = params.A_ref      ?? (params.CdA / (params.Cd || 0.28));  // 기준 면적 [m²]
    F_lift = 0.5 * rho * Cl * Math.max(1.5, A_ref) * v * v;  // [N] 양수=양력
  }

  // ✅ [FIX] 공기역학 다운포스 ↔ Fz 실시간 루프 연결
  // ClA (양력면적): 음수 = 다운포스(접지력 증가), 양수 = 양력(접지력 감소)
  // aero_lift_enable 게이팅 없이 항상 계산 — ClA 기본값 0 → 기존 동작 보존
  const _ClA      = params.ClA ?? 0;
  const _F_down   = -0.5 * rho * _ClA * v * v;       // 양수 = 다운포스
  const _bal_f    = params.aero_balance_f ?? 0.35;    // 전륜 배분 비율
  const dFz_aero_f = _F_down * _bal_f;                // 전륜 수직력 증가 [N]
  const dFz_aero_r = _F_down * (1.0 - _bal_f);        // 후륜 수직력 증가 [N]

  return {
    F_aero, F_roll, F_grade, F_rough,
    F_total: F_aero + F_roll + F_grade + F_rough,
    F_lift,                                       // [Low-Lift] 공기 양력/다운포스 [N]
    Fz_aero_per_corner: +(-F_lift / 4).toFixed(2), // 코너당 수직력 변화 (양력→음, 다운포스→양)
    // ✅ [FIX] 다운포스 전/후륜 배분 (computeVerticalLoad_v4 입력용)
    F_down:      +_F_down.toFixed(1),
    dFz_aero_f:  +dFz_aero_f.toFixed(1),
    dFz_aero_r:  +dFz_aero_r.toFixed(1),
    rho_eff: rho, Crr_eff: Crr,
    delta_Crr_pressure, delta_Crr_IRI,
    CdA_v3: cdaResult.CdA_v3,
    shutter_open: cdaResult.shutter_open,
    v_eff_wind: cwResult.v_eff,
    delta_F_wind: cwResult.delta_F_wind || 0,
    crosswind_active: params.crosswind_enable && (params.v_wind || 0) > 0
  };
}

// ── rlResist ──
/**
 * 정속 주행(Road Load) 조건에서 합산 도로 저항력을 계산한다.
 *
 * resistanceForcesV3의 경량 래퍼로, opts 인수로 임시 파라미터 오버라이드를 지원한다.
 * 탭(RLTabContent)과 통합분석(AdvancedFinalTabContent)에서 속도 배열을 순환할 때 사용한다.
 *
 * opts:
 *   grade_rad  : 경사각 [rad]
 *   T_amb      : 외기온 [°C]
 *   T_tire     : 타이어 온도 [°C]  ← opts 미지정 시 params.tire_temp_nominal → T_amb 순 폴백
 *   v_wind     : 맞바람 [m/s]
 *   CdA_delta  : CdA 임시 오프셋 [m²]
 *   Crr_delta  : Crr 임시 오프셋
 *   mass_delta : 질량 임시 오프셋 [kg]
 *
 * @param {number} v       차속 [m/s]
 * @param {object} params  차량 파라미터
 * @param {object} [opts]  임시 오버라이드 옵션
 * @returns {{ F_total, F_aero, F_roll, F_grade, ... }}  도로 저항력 분해 결과
 */
function rlResist(v, params, opts) {
  let _opts = opts || {};
  let grade_rad  = _opts.grade_rad  || 0;
  let T_amb_     = _opts.T_amb      !== undefined ? _opts.T_amb : (params.T_amb || 25);
  // [Fix-WARN-1] T_tire를 opts에서 명시적으로 수신.
  //   수정 전: CrrDynamic 두 번째 인수에 T_amb_를 그대로 전달 → 타이어 온도 ≠ 외기 온도 혼용
  //   수정 후: opts.T_tire가 있으면 사용, 없으면 params.tire_temp_nominal → T_amb_ 순서로 폴백.
  //   폴백 전략 근거:
  //     · opts.T_tire: 시뮬레이션 stepVehiclePhysics에서 전달되는 실시간 타이어 온도 (최우선)
  //     · params.tire_temp_nominal: 정적 분석(rlBuildR1/R2 등)에서 사용자가 지정한 공칭 온도
  //     · T_amb_: 위 둘 모두 없을 때의 최종 폴백 (기존 동작 유지, 하위 호환)
  let T_tire_    = _opts.T_tire     !== undefined ? _opts.T_tire
                 : (params.tire_temp_nominal     !== undefined ? params.tire_temp_nominal
                 : T_amb_);
  let v_wind     = _opts.v_wind     || 0;
  let CdA_delta  = _opts.CdA_delta  || 0;
  let Crr_delta  = _opts.Crr_delta  || 0;
  let mass_delta = _opts.mass_delta || 0;
  let rho   = rhoCorrected(T_amb_, params.altitude || 0);
  // [A33-R5] road_condition → Crr 노면 보정 계수 (wet +15%, snow +150%, ice +80%)
  let _road_cond = params.road_condition || "dry";
  let _crr_road_k = { dry: 1.0, wet: 1.15, snow: 2.50, ice: 1.80 }[_road_cond] || 1.0;
  let Crr   = CrrDynamic(v, T_tire_, params) * (1 + Crr_delta) * _crr_road_k;
  let CdA   = (params.CdA || 0.49) + CdA_delta;
  let mass  = (params.m || 1850) + mass_delta;
  let v_air = v + v_wind;
  let F_aero  = 0.5 * rho * CdA * Math.abs(v_air) * v_air;
  let F_roll  = mass * g * Crr * Math.cos(grade_rad);
  let F_grade = mass * g * Math.sin(grade_rad);
  return { F_aero: F_aero, F_roll: F_roll, F_grade: F_grade,
           F_total: F_aero + F_roll + F_grade, rho: rho, Crr: Crr, CdA: CdA };
}

// ── rlSteadyPower ──
/**
 * 정속 주행에 필요한 모터 입력 전력과 에너지 소비를 계산한다.
 *
 * P_wheel = F_total × v
 * P_elec  = P_wheel / (η_driveline × η_motor_inv) + P_aux
 * e_wltp  = P_elec / v [Wh/m] → 100km 기준: × 100000/3600 [kWh/100km]
 *
 * @param {number} v       차속 [m/s]
 * @param {object} params  차량 파라미터
 * @param {object} [opts]  rlResist와 동일한 임시 오버라이드 옵션
 * @returns {{ P_wheel, P_elec, e_wltp, range_km, F_total, ... }}
 */
function rlSteadyPower(v, params, opts) {
  let _opts = opts || {};
  let P_aux_kW = _opts.P_aux_kW || 0;
  let res   = rlResist(v, params, opts);
  let eta   = (params.eta_driveline || 0.95) * (params.eta_motor_inv || 0.91);
  // [Fix-ECO-eta] drive_mode 효율 보정: 승산(×eta_mode) → 가산(+delta_eta) + 물리 상한 클램프
  //
  // 기존 방식의 문제:
  //   eta_mode = 1.04(ECO)를 eta에 직접 곱하면 기본값(0.865)에서 0.900이 되어
  //   물리적으로 성립하지만, eta_dl=0.98·eta_inv=0.97 같은 고성능 파라미터에서는
  //   0.952 × 1.04 = 0.990 → η > 1 초과 발생 가능.
  //
  // 수정 방식:
  //   ECO 모드가 실제로 하는 일 = 토크 요청을 줄여 모터를 고효율 운전점(낮은 Iq 영역)으로
  //   이동시키는 것. 이를 delta_eta(가산 이득)로 근사하되, eta_combined_max로 물리 상한 보장.
  //
  //   ECO      : +3.5% (저손실점 이동 — 동손 ∝ Iq²  감소)
  //   SPORT    : −4.0% (고부하 영역 — 철손+동손 급증)
  //   SNOW     : +2.0% (저속·저토크 구간 위주)
  //   OFF_ROAD : −5.0% (슬립·불균일 부하 → 전체 효율 저하)
  //   NORMAL   :  0.0%
  //
  //   eta_combined_max: η_dl × η_inv 의 물리적 최대치 (기본 0.96 = 0.98 × 0.98)
  let mode = (params && params.drive_mode) || 'NORMAL';
  let eta_combined_max = params.eta_combined_max || 0.96;
  let delta_eta = mode === 'ECO'      ? (params.delta_eta_eco      ||  0.035)
                : mode === 'SPORT'    ? (params.delta_eta_sport     || -0.040)
                : mode === 'SNOW'     ? (params.delta_eta_snow      ||  0.020)
                : mode === 'OFF_ROAD' ? (params.delta_eta_offroad   || -0.050)
                : 0;
  eta = Math.min(eta_combined_max, eta + delta_eta);
  // [A21-02] ECO/SNOW 모드: P_aux 절약
  let P_aux_mode = P_aux_kW;
  if (mode === 'ECO')  P_aux_mode = P_aux_kW * 0.85;
  if (mode === 'SNOW') P_aux_mode = P_aux_kW * 0.90;
  let P_bat = (v > 0.01 ? res.F_total * v / eta : 0) + P_aux_mode * 1000;
  let Wh_km = v > 0.5 ? P_bat / (v * 3.6) : 0;
  // [BugFix-v16-2] eta_dl_ / eta_inv_ dead code 제거: 반환값에 미사용 미정의 변수 정리
  // P_wheel_kW: 순수 도로부하 기계 출력 (kW) — 휠 출력 기준, η 역산 불필요
  let P_wheel_kW = v > 0.01 ? res.F_total * v / 1000 : 0;
  return { P_batt_kW: P_bat / 1000, P_wheel_kW: P_wheel_kW, Wh_km: Wh_km, F_total: res.F_total };
}

// ── rlPmaxAtV ──
/**
 * 속도 v [m/s]에서의 최대 휠 출력을 산출한다 (세 제약의 최솟값).
 * 제약: ① T_max_motor×ω×η_dl, ② P_max_wheel, ③ P_max_battery×η_inv×η_dl.
 * 듀얼 모터: motor_front_power_ratio로 전/후 모터 개별 병목을 계산.
 *
 * @param {number} v       차속 [m/s]
 * @param {object} params  T_max_motor, P_max_wheel, P_max_battery, gear_ratio 등
 * @returns {number}       최대 휠 출력 [kW] (항상 휠 기준)
 */
function rlPmaxAtV(v, params) {
  let eta_dl  = params.eta_driveline || 0.95;
  let eta_inv = params.eta_motor_inv || 0.91;
  let omega_m = Math.max(0.5, v / (params.r_wheel || 0.33)) * (params.gear_ratio || 9.5); // 모터 각속도 [rad/s]
  // [A21-14-01] 세 제약을 모두 휠 출력 [W] 기준으로 통일 후 min() → 항상 휠 출력 반환
  //   기존 T_pk×ω 방식: T_max_motor binding 시 모터 샤프트 출력 반환 (η_dl 누락 → 기준 혼합)
  //   수정: 각 제약을 휠 출력 [W] 단위로 먼저 변환 후 비교
  let P_lim_Tmax  = (params.T_max_motor   || 400)    * omega_m * eta_dl;     // 모터토크→휠 출력 [W]
  let P_lim_wheel = (params.P_max_wheel   || 220000);                          // 휠 스펙 그대로  [W]
  let P_lim_batt  = (params.P_max_battery || 250000) * eta_inv * eta_dl;     // 배터리→휠 출력 [W]

  // [A35-BUG-M1] 듀얼 모터: motor_front_power_ratio로 전/후 모터 개별 출력 상한 추가
  // 싱글 모터: P_max_wheel이 이미 단일 모터 스펙 → 추가 제약 없음
  let _motor_cfg = params.motor_config || 'dual';
  if (_motor_cfg === 'dual' && params.motor_front_power_ratio != null) {
    let _rfp = params.motor_front_power_ratio;          // 전륜 출력 비율 (0~0.6)
    let _rrp = 1.0 - _rfp;                              // 후륜 출력 비율
    // torque_split_front 기준 실제 분배비와 비교하여 병목 모터를 찾음
    let _ts  = params.torque_split_front != null ? params.torque_split_front : 0.4;
    // 전륜: 요청 토크 비율(_ts)이 전륜 출력 비율(_rfp) 초과 시 전륜이 병목
    // 후륜: 요청 토크 비율(1-_ts)이 후륜 출력 비율(_rrp) 초과 시 후륜이 병목
    let _P_front_lim = _rfp > 0.01 ? P_lim_wheel * _rfp / Math.max(_ts,  0.01) : Infinity;
    let _P_rear_lim  = _rrp > 0.01 ? P_lim_wheel * _rrp / Math.max(1 - _ts, 0.01) : Infinity;
    let P_lim_axle = Math.min(_P_front_lim, _P_rear_lim);
    P_lim_wheel = Math.min(P_lim_wheel, P_lim_axle);
  }

  return Math.min(P_lim_Tmax, P_lim_wheel, P_lim_batt) / 1000;              // kW (항상 휠 기준)
}

// ── rlCalcVcross ──
/**
 * 공기저항과 구름저항이 같아지는 교차 속도를 이분탐색으로 계산한다.
 *
 * 교차 속도: F_aero(v*) = F_roll → ½ρCdA·v*² = m·g·Crr
 * 이 속도 이상에서는 공기저항이 지배적, 이하에서는 구름저항이 지배적.
 * 전비 최적화의 핵심 지표로 경제 속도(V_opt) 계산의 기준점이 된다.
 *
 * @param {object} params  차량 파라미터
 * @returns {number}       교차 속도 [km/h]
 */
function rlCalcVcross(params) {
  // [BugFix-v16-1] 이분탐색으로 F_aero(v) = F_roll(v, CrrDynamic) 교차점 수치 풀이
  // 기존: params.Crr 고정 상수 사용 → v_cross가 실제 그래프보다 5~8 km/h 낮게 표시됨
  // 수정: CrrDynamic(v, T_tire)를 적용한 이분탐색으로 실제 교차속도 정확 산출
  // [A68-Fix-3] road_condition 노면 보정 추가:
  //   수정 전: CrrDynamic 기본값만 사용 → wet/snow/ice 노면에서 v_cross KPI가 실제보다 낮게 표시
  //   수정 후: rlResist 와 동일한 _crr_road_k 계수를 Crr 에 적용 → 노면 조건 일관성 확보
  // [Fix-WARN-1] CrrDynamic 타이어 온도 인수 교정:
  //   수정 전: T_amb (외기 온도) 전달 → 정적 분석(v_cross KPI)에서 T_tire ≠ T_amb 혼용
  //   수정 후: params.tire_temp_nominal (사용자 지정 공칭 타이어 온도) → T_amb 폴백
  //   rlCalcVcross는 정속 열평형 상태를 가정하므로 공칭 온도가 물리적으로 적합
  let rho   = rhoCorrected(params.T_amb || 25, params.altitude || 0);
  let T_amb = params.T_amb || 25;
  let T_tire_vc = params.tire_temp_nominal !== undefined ? params.tire_temp_nominal : T_amb;
  let _road_cond  = params.road_condition || "dry";
  let _crr_road_k = { dry: 1.0, wet: 1.15, snow: 2.50, ice: 1.80 }[_road_cond] || 1.0;
  // F_aero - F_roll > 0 이 되는 속도를 탐색 (단위: m/s)
  // F_aero(v) = 0.5 * rho * CdA * v^2
  // F_roll(v) = m * g * CrrDynamic(v, T_tire) * _crr_road_k
  function diff(v_ms) {
    let F_aero = 0.5 * rho * params.CdA * v_ms * v_ms;
    // [Fix-⑥] typeof CrrDynamic 가드 제거 — 동일 파일 내 함수, 항상 존재
    let Crr_v  = CrrDynamic(v_ms, T_tire_vc, params) * _crr_road_k;
    let F_roll = params.m * g * Crr_v;
    return F_aero - F_roll;
  }
  // 탐색 범위: 1 ~ 250 km/h (m/s 변환)
  let lo = 1 / 3.6, hi = 250 / 3.6;
  // [BugFix-v16-1b] 이분탐색 전제조건 양쪽 경계 모두 검증
  // diff(lo) >= 0: 1 km/h 이하에서 이미 F_aero >= F_roll → v_cross < 1 km/h (물리적 비현실)
  if (diff(lo) >= 0) {
    return lo * 3.6; // ≈ 1 km/h (극단적 파라미터 방어)
  }
  // diff(hi) < 0: 250 km/h에서도 F_roll > F_aero → 범위 내 교차점 없음
  if (diff(hi) < 0) {
    // 고정 Crr fallback
    let vsq = (params.m * g * params.Crr) / (0.5 * rho * params.CdA);
    return Math.sqrt(Math.max(0, vsq)) * 3.6;
  }
  // 이분탐색 전제 보장: diff(lo) < 0 && diff(hi) > 0
  // 30회 반복 → 정밀도 ~0.001 m/s
  for (let i = 0; i < 30; i++) {
    let mid = (lo + hi) / 2;
    if (diff(mid) < 0) lo = mid; else hi = mid;
  }
  return ((lo + hi) / 2) * 3.6;
}

// ── rlCalcVopt ──
/**
 * 전비(kWh/100km)가 최소화되는 최적 경제 속도를 계산한다.
 *
 * 20~200 km/h 범위를 1 km/h 간격으로 탐색하여 e_wltp 최솟값을 찾는다.
 * 보조부하(P_aux), 경사도, 맞바람 조건을 반영한다.
 *
 * @param {object} params         차량 파라미터
 * @param {number} [P_aux_kW=0]  보조부하 [kW]
 * @param {number} [grade_pct=0] 경사도 [%]
 * @param {number} [headwind_ms=0] 맞바람 [m/s]
 * @returns {number}              최적 경제 속도 [km/h]
 */
function rlCalcVopt(params, P_aux_kW, grade_pct, headwind_ms) { // [Fix-R3-GradeWind] grade·headwind 추가
  let grade_r  = (grade_pct   !== undefined ? grade_pct   : 0);
  let v_wind_r = (headwind_ms !== undefined ? headwind_ms : 0);
  let grade_rad_opt = Math.atan(grade_r / 100);
  let best = { v: 60, Wh: 9999 };
  for (let vk = 10; vk <= 180; vk++) {
    let r = rlSteadyPower(vk / 3.6, params, { P_aux_kW: P_aux_kW, grade_rad: grade_rad_opt, v_wind: v_wind_r });
    if (r.Wh_km > 0 && r.Wh_km < best.Wh) best = { v: vk, Wh: +r.Wh_km.toFixed(1) };
  }
  return best;
}

// ── rlCalcVmax ──
/**
 * 최고 속도를 1 km/h 간격 루프로 탐색한다 (P_motor ≥ P_road 조건).
 * [A21-14-02] 휠 기준 출력(F_total×v)으로 통일 → eta_dl 역산 불필요.
 * 소프트웨어 속도 제한(v_max_sw) 캡 포함.
 *
 * @param {object} params         차량 파라미터
 * @param {number} [mass_delta_kg=0]   추가 탑승/적재 질량 [kg]
 * @param {number} [grade_pct=0]       경사도 [%]
 * @param {number} [throttle_pct=100]  개도 [%]
 * @param {number} [headwind_ms=0]     맞바람 속도 [m/s]
 * @returns {{ v_max, P_road, P_motor, P_margin, load_pct }}
 */
function rlCalcVmax(params, mass_delta_kg, grade_pct, throttle_pct, headwind_ms) {
  let mdelta    = (mass_delta_kg !== undefined && mass_delta_kg > 0) ? mass_delta_kg : 0;
  let gpct      = (grade_pct !== undefined ? grade_pct : 0);
  let grade_rad = Math.atan(gpct / 100);
  let v_wind_vm = (headwind_ms !== undefined && headwind_ms > 0) ? headwind_ms : 0; // [Fix-RL1]
  let thr_frac  = (throttle_pct !== undefined ? Math.max(0.05, Math.min(1.0, throttle_pct / 100)) : 1.0); // [Advance29-WARN01]
  // [A21-14-02] eta_dl 제거: P_rd = F_total×v/1000 (휠 기준)으로 변경 → eta_dl 역산 불필요
  // [A21-11-02] drive_mode 피크 파워 스케일
  let mode = (params && params.drive_mode) || 'NORMAL';
  let pkScale = getLUTPkScale(params, mode); // [BugFix-A05-01] LUT ON 시 자동 산출
  // [A21-11-05] 소프트웨어 속도 제한 캡
  let v_sw_cap = (params.v_max_sw && params.v_max_sw > 0) ? params.v_max_sw : 400;
  let v_max_kmh = 0;
  let P_road_at_vmax = 0;
  let P_motor_at_vmax = 0;
  for (let vk = 1; vk <= 400; vk++) {
    if (vk > v_sw_cap) break;  // [A21-11-05] 소프트웨어 속도 제한
    let v    = vk / 3.6;
    let res  = rlResist(v, params, { grade_rad: grade_rad, mass_delta: mdelta, v_wind: v_wind_vm }); // [Fix-RL1] v_wind 전달
    // [A21-14-02] P_rd: 순수 휠 기계 출력 기준 (F_total × v / 1000)
    let P_rd = res.F_total * v / 1000;                                 // kW (휠 기계 출력 기준)
    let P_pk = rlPmaxAtV(v, params) * pkScale * thr_frac;             // [Advance29-WARN01] thr_frac 적용 — 개도 반영
    if (P_pk >= P_rd) {
      v_max_kmh        = vk;
      P_road_at_vmax   = P_rd;
      P_motor_at_vmax  = P_pk;
    } else {
      break;  // P_motor < P_road → 이 속도 이상은 정속 불가
    }
  }
  // 여유 전력 @v_max
  let P_margin = Math.max(0, P_motor_at_vmax - P_road_at_vmax);
  return {
    v_max:    v_max_kmh,
    P_road:   +P_road_at_vmax.toFixed(1),
    P_motor:  +P_motor_at_vmax.toFixed(1),
    P_margin: +P_margin.toFixed(1),
    load_pct: P_motor_at_vmax > 0 ? +(P_road_at_vmax / P_motor_at_vmax * 100).toFixed(0) : 100,
  };
}

// ── rlCalcVmaxTorque ──
/**
 * 휠 토크 기준 최고 속도를 탐색한다 (T_drive_wheel ≥ T_resist_wheel 조건).
 * [A21-15-05] rlPmaxAtV로 P_max_battery 포함 세 제약을 휠 출력으로 통일 후 모터 토크 역산.
 * drive_mode별 driveModeThrottle() 및 pkScale 반영.
 *
 * @param {object} params            차량 파라미터
 * @param {number} [throttle_pct=100] 개도 [%]
 * @param {number} [mass_delta_kg=0]  추가 질량 [kg]
 * @param {number} [grade_pct=0]      경사도 [%]
 * @param {number} [headwind_ms=0]    맞바람 [m/s]
 * @returns {{ v_max_torque, T_drive, T_resist, omega_max }}
 */
function rlCalcVmaxTorque(params, throttle_pct, mass_delta_kg, grade_pct, headwind_ms) {
  let thr_raw   = (throttle_pct !== undefined ? throttle_pct : 100) / 100;
  // [A21-02] drive_mode 반영: driveModeThrottle으로 ECO/NORMAL/SPORT 특성 적용
  let thr       = driveModeThrottle(thr_raw, params);
  let mdelta    = (mass_delta_kg !== undefined && mass_delta_kg > 0) ? mass_delta_kg : 0;
  let gpct      = (grade_pct !== undefined ? grade_pct : 0);
  let grade_rad = Math.atan(gpct / 100);
  let v_wind_vmt = (headwind_ms !== undefined && headwind_ms > 0) ? headwind_ms : 0; // [Fix-RL2]

  let gear_ratio  = params.gear_ratio    || 9.5;
  let r_wheel     = params.r_wheel       || 0.33;
  let eta_dl      = params.eta_driveline || 0.95;
  let T_max_m     = params.T_max_motor   || 400;
  // [A21-15-05] P_max_motor 직접 계산 제거 → rlPmaxAtV 기반으로 교체
  //   기존: P_max_motor = P_max_wheel / eta_dl  (배터리 출력 제한 P_max_battery 미반영)
  //         → 배터리 바인딩 조건에서 T_motor_max 과대 계산 → v_max_torque 과대 추정
  //   수정: rlPmaxAtV(v, params) [A21-14-01 수정 버전]를 루프 내에서 사용
  //         → P_max_wheel, P_max_battery, T_max_motor 세 제약 모두 반영, 휠 출력 기준
  //         → T_motor = P_wheel_max / (omega_m × eta_dl)  [모터 샤프트 토크로 역산]
  // [A21-13-02] pkScale: drive_mode 반영 → rlCalcVmax / rlBuildR4 와 동일 스케일 적용
  let _mode_vmt   = (params && params.drive_mode) || 'NORMAL';
  let pkScale_vmt = getLUTPkScale(params, _mode_vmt); // [BugFix-A05-01] LUT ON 시 자동 산출
  // [A21-11-05] 소프트웨어 속도 제한 캡
  let v_sw_cap    = (params.v_max_sw && params.v_max_sw > 0) ? params.v_max_sw : 400;

  let v_max_torque      = 0;
  let T_drive_at_vmax   = 0;
  let T_resist_at_vmax  = 0;

  for (let vk = 1; vk <= 400; vk++) {
    if (vk > v_sw_cap) break;  // [A21-11-05] 소프트웨어 속도 제한
    let v           = vk / 3.6;                       // m/s
    let omega_wheel = v / r_wheel;                    // rad/s (wheel)
    let omega_motor = omega_wheel * gear_ratio;       // rad/s (motor)

    // ① 구동 토크 (휠 접지면 기준)
    // [A21-15-05] rlPmaxAtV → 휠 출력 기준 [W] → 모터 샤프트 토크 역산
    let P_wheel_max_vmt = rlPmaxAtV(v, params) * pkScale_vmt * 1000;  // W (휠 출력, 배터리 제한 포함)
    let T_motor_max;
    if (omega_motor > 0.5) {
      T_motor_max = Math.min(T_max_m * pkScale_vmt, P_wheel_max_vmt / (omega_motor * eta_dl));
    } else {
      T_motor_max = T_max_m * pkScale_vmt;
    }
    // [A68-Fix-2] thr_clip 방식을 rlBuildR1 과 통일:
    //   수정 전: Math.min(1.0, thr) → SPORT 모드(thr=1.15)에서 1.0 클리핑 → T_drive 과소 → R1 차트 불일치
    //   수정 후: Math.min(pkScale_vmt + 0.05, thr) → rlBuildR1 thr_clip 과 동일 상한 허용
    let thr_clip_vmt  = Math.min(pkScale_vmt + 0.05, thr);
    let T_drive_wheel = T_motor_max * thr_clip_vmt * gear_ratio * eta_dl;  // (N·m)

    // ② 주행저항 환산 휠 토크
    let res           = rlResist(v, params, { grade_rad: grade_rad, mass_delta: mdelta, v_wind: v_wind_vmt }); // [Fix-RL2]
    let T_resist_wheel = res.F_total * r_wheel;                    // (N·m)

    if (T_drive_wheel >= T_resist_wheel) {
      v_max_torque     = vk;
      T_drive_at_vmax  = T_drive_wheel;
      T_resist_at_vmax = T_resist_wheel;
    } else {
      break;  // 구동 토크 < 저항 토크 → 이 속도 이상 정속 불가
    }
  }

  let T_margin       = Math.max(0, T_drive_at_vmax - T_resist_at_vmax);
  let torque_load_pct = T_drive_at_vmax > 0
    ? +(T_resist_at_vmax / T_drive_at_vmax * 100).toFixed(0) : 100;

  return {
    v_max_torque:      v_max_torque,                        // [km/h]
    T_drive:           +T_drive_at_vmax.toFixed(1),         // (N·m) 구동 토크 at v_max
    T_resist:          +T_resist_at_vmax.toFixed(1),        // (N·m) 저항 토크 at v_max
    T_margin:          +T_margin.toFixed(1),                // (N·m) 토크 여유
    torque_load_pct:   torque_load_pct,                     // (%)   토크 부하율
  };
}

// ── _rlVLoop — 내부 헬퍼 ──
// [Fix-⑥] rlBuildR1~R4 공통 루프 구조 통합:
//   vMax 결정, data 배열 초기화, vk→v 변환, push/return 패턴이 4개 함수에 중복됨.
/**
 * rlBuildR1~R4 공통 속도 루프 스캐폴딩.
 * @param {object} params     v_max_chart 파라미터
 * @param {number} vStart     시작 속도 [km/h]
 * @param {number} vStep      속도 증분 [km/h]
 * @param {Function} rowBuilder  (v_ms, v_kmh) → object|null  (null 시 스킵)
 * @returns {object[]}  각 속도점 데이터 배열
 */
function _rlVLoop(params, vStart, vStep, rowBuilder) {
  const vMax = Math.max(300, params.v_max_chart || 300);
  const data = [];
  for (let vk = vStart; vk <= vMax; vk += vStep) {
    const row = rowBuilder(vk / 3.6, vk);
    if (row != null) data.push(row);
  }
  return data;
}

// ── rlBuildR1 ──
/**
 * 도로 부하 차트 R1(구동력 vs 속도)용 시계열 데이터를 생성한다.
 * 구동 토크, 주행저항, 초과 가속 토크를 속도별로 계산하여 반환.
 * [A41-BUG2-FIX] headwind_ms, mass_delta, grade 반영.
 *
 * @param {object} params          차량 파라미터
 * @param {number} [throttle_pct]  개도 [%] (기본 100)
 * @param {number} [mass_delta_kg] 추가 질량 [kg]
 * @param {number} [grade_pct]     경사도 [%]
 * @param {number} [headwind_ms]   맞바람 [m/s]
 * @returns {object[]}  각 속도 지점 { v, F_drive, F_resist, F_excess, ... }
 */
function rlBuildR1(params, throttle_pct, mass_delta_kg, grade_pct, headwind_ms) { // [A41-BUG2-FIX] headwind_ms 추가
  const T          = params.T_amb || 25;
  const thr_raw    = (throttle_pct !== undefined ? throttle_pct : 100) / 100;
  const thr        = driveModeThrottle(thr_raw, params);
  const mdelta     = (mass_delta_kg !== undefined && mass_delta_kg > 0) ? mass_delta_kg : 0;
  const gpct       = (grade_pct !== undefined ? grade_pct : 0);
  const v_wind_r1  = (headwind_ms !== undefined && headwind_ms > 0) ? headwind_ms : (params.wind_speed_ms || 0);
  const grade_rad  = Math.atan(gpct / 100);
  const gear_ratio = params.gear_ratio   || 9.5;
  const r_wheel    = params.r_wheel      || 0.33;
  const eta_dl     = params.eta_driveline || 0.95;
  const T_max_motor = params.T_max_motor || 400;
  const _mode_r1   = (params && params.drive_mode) || 'NORMAL';
  const pkScale_r1 = getLUTPkScale(params, _mode_r1);
  const thr_clip   = Math.min(pkScale_r1 + 0.05, thr);

  // [Fix-⑥] _rlVLoop으로 공통 루프 스캐폴딩 통합
  return _rlVLoop(params, 0, 2, (v, vk) => {
    const base  = rlResist(v, params, { T_amb: T, grade_rad, v_wind: v_wind_r1 });
    const mvar  = mdelta > 0 ? rlResist(v, params, { T_amb: T, grade_rad, mass_delta: mdelta, v_wind: v_wind_r1 }) : null;
    const flat  = gpct !== 0 ? rlResist(v, params, { T_amb: T, grade_rad: 0, v_wind: v_wind_r1 }) : null;
    const omega_wheel   = v > 0.01 ? v / r_wheel : 0;
    const T_wheel_torque = T_max_motor * pkScale_r1 * thr_clip * gear_ratio * eta_dl;
    const T_wheel_power  = omega_wheel > 0.05
      ? ((params.P_max_wheel || 220000) * pkScale_r1 * thr_clip) / omega_wheel
      : T_wheel_torque;
    const T_wheel_drive  = Math.min(T_wheel_torque, T_wheel_power);
    const T_motor_max    = T_wheel_drive / Math.max(gear_ratio * eta_dl, 1e-9);
    const P_motor_wheel_kW = rlPmaxAtV(v, params) * pkScale_r1 * thr_clip;
    const P_road_kW        = v > 0.01 ? base.F_total * v / 1000 : 0;
    return {
      v_kmh:           vk,
      P_roll:          +(base.F_roll  * v / 1000).toFixed(3),
      P_aero:          +(base.F_aero  * v / 1000).toFixed(3),
      P_grade:         +(base.F_grade * v / 1000).toFixed(3),
      P_total:         +(base.F_total * v / 1000).toFixed(3),
      P_total_flat:    flat ? +(flat.F_total * v / 1000).toFixed(3) : null,
      P_roll_mvar:     mvar ? +(mvar.F_roll  * v / 1000).toFixed(3) : null,
      P_total_mvar:    mvar ? +(mvar.F_total * v / 1000).toFixed(3) : null,
      F_roll:          +base.F_roll.toFixed(1),
      F_aero:          +base.F_aero.toFixed(1),
      F_grade:         +base.F_grade.toFixed(1),
      F_total:         +base.F_total.toFixed(1),
      T_drive_wheel:   +T_wheel_drive.toFixed(1),
      T_drive_torque:  +T_wheel_torque.toFixed(1),
      T_drive_power:   +T_wheel_power.toFixed(1),
      T_motor_cmd:     +T_motor_max.toFixed(1),
      T_resist_wheel:  +(base.F_total * r_wheel).toFixed(1),
      T_grade_wheel:   +(base.F_grade * r_wheel).toFixed(1),
      P_motor_wheel:   +P_motor_wheel_kW.toFixed(2),
      P_road_kW:       +P_road_kW.toFixed(2),
    };
  });
}

// ── rlBuildR2base ──
/**
 * 도로 부하 차트 R2(정속 전비 vs 속도, 보조부하 0~3 kW)용 데이터를 생성한다.
 * 각 속도에서 P_aux=0/1/2/3 kW별 Wh/km과 배터리 출력을 계산.
 *
 * @param {object} params       차량 파라미터
 * @param {number} [headwind_ms]  맞바람 [m/s]
 * @param {number} [grade_pct]    경사도 [%]
 * @returns {object[]}  각 속도 지점 { v_kmh, Wh_0kW, Wh_1kW, Wh_2kW, Wh_3kW, ... }
 */
function rlBuildR2base(params, headwind_ms, grade_pct) {
  const v_wind_r2  = (headwind_ms !== undefined && headwind_ms > 0) ? headwind_ms : (params.wind_speed_ms || 0);
  const grade_rad  = Math.atan((grade_pct !== undefined ? grade_pct : 0) / 100);
  // [Fix-⑥] _rlVLoop으로 공통 루프 스캐폴딩 통합 (vStart=5 유지)
  return _rlVLoop(params, 5, 2, (v, vk) => {
    const row = { v_kmh: vk };
    [0, 1, 2, 3].forEach(pa => {
      const r = rlSteadyPower(v, params, { P_aux_kW: pa, v_wind: v_wind_r2, grade_rad });
      row['Wh_' + pa + 'kW'] = +r.Wh_km.toFixed(1);
      row['P_'  + pa + 'kW'] = +r.P_batt_kW.toFixed(2);
    });
    return row;
  });
}

// ── rlBuildR3 ──
/**
 * 도로 부하 차트 R3(파라미터 민감도) 데이터를 생성한다.
 * 기준 대비 Crr±10%, CdA−5/10%, 맞바람+5/10 m/s, 냉각그릴 개방 등 시나리오 비교.
 *
 * @param {object} params       차량 파라미터
 * @param {number} [grade_pct]     경사도 [%]
 * @param {number} [base_headwind] 기준 맞바람 [m/s]
 * @returns {object[]}  각 속도 지점 { v_kmh, P_base, P_Crr_p10, P_CdA_m5, ... }
 */
function rlBuildR3(params, grade_pct, base_headwind) {
  const T        = params.T_amb || 25;
  const gr_pct   = (grade_pct     !== undefined ? grade_pct     : 0);
  const v_base   = (base_headwind !== undefined ? base_headwind : 0);
  const grade_rad = Math.atan(gr_pct / 100);
  // [Fix-⑥] _rlVLoop으로 공통 루프 스캐폴딩 통합
  return _rlVLoop(params, 0, 2, (v, vk) => {
    const base   = rlSteadyPower(v, params, { T_amb: T, grade_rad, v_wind: v_base });
    const crrP10 = rlSteadyPower(v, params, { T_amb: T, grade_rad, v_wind: v_base, Crr_delta:  0.10 });
    const crrM10 = rlSteadyPower(v, params, { T_amb: T, grade_rad, v_wind: v_base, Crr_delta: -0.10 });
    const cdaM5  = rlSteadyPower(v, params, { T_amb: T, grade_rad, v_wind: v_base, CdA_delta: -(params.CdA * 0.05) });
    const cdaM10 = rlSteadyPower(v, params, { T_amb: T, grade_rad, v_wind: v_base, CdA_delta: -(params.CdA * 0.10) });
    const wind5  = rlSteadyPower(v, params, { T_amb: T, grade_rad, v_wind: v_base + 5  });
    const wind10 = rlSteadyPower(v, params, { T_amb: T, grade_rad, v_wind: v_base + 10 });
    const cool   = rlSteadyPower(v, params, { T_amb: T, grade_rad, v_wind: v_base, CdA_delta: (params.CdA_cool_open || 0.04) });
    return {
      v_kmh:     vk,
      P_base:    +base.P_batt_kW.toFixed(2),
      P_Crr_p10: +crrP10.P_batt_kW.toFixed(2),
      P_Crr_m10: +crrM10.P_batt_kW.toFixed(2),
      P_CdA_m5:  +cdaM5.P_batt_kW.toFixed(2),
      P_CdA_m10: +cdaM10.P_batt_kW.toFixed(2),
      P_wind5:   +wind5.P_batt_kW.toFixed(2),
      P_wind10:  +wind10.P_batt_kW.toFixed(2),
      P_cool:    +cool.P_batt_kW.toFixed(2),
      Wh_base:   +base.Wh_km.toFixed(1),
    };
  });
}

// ── rlBuildR4 ──
/**
 * 도로 부하 차트 R4(주행 시나리오별 부하율) 데이터를 생성한다.
 * 시나리오: 평지, 3% 경사, 맞바람 5 m/s, 사용자 지정(grade+wind).
 * 각 시나리오별 배터리 출력, 부하율(%), 여유 출력을 반환.
 *
 * @param {object} params          차량 파라미터
 * @param {number} [user_grade_pct]  사용자 경사도 [%]
 * @param {number} [user_v_wind]     사용자 맞바람 [m/s]
 * @returns {object[]}  각 속도 지점 { v_kmh, P_peak_kW, load_flat, P_grade, ... }
 */
function rlBuildR4(params, user_grade_pct, user_v_wind) {
  const _ugp = (user_grade_pct !== undefined ? user_grade_pct : 0);
  const _uvw = (user_v_wind    !== undefined ? user_v_wind    : 0);
  const scenarios = [
    { key: 'flat',  grade_pct: 0,    v_wind: 0    },
    { key: 'grade', grade_pct: 3,    v_wind: 0    },
    { key: 'wind',  grade_pct: 0,    v_wind: 5    },
    { key: 'user',  grade_pct: _ugp, v_wind: _uvw },
  ];
  const mode    = (params && params.drive_mode) || 'NORMAL';
  const pkScale = getLUTPkScale(params, mode);
  // [Fix-⑥] _rlVLoop으로 공통 루프 스캐폴딩 통합
  return _rlVLoop(params, 0, 2, (v, vk) => {
    const P_pk   = rlPmaxAtV(v, params) * pkScale;
    const P_cont = P_pk * (params.P_cont_ratio || 0.70);
    const row    = { v_kmh: vk, P_peak_kW: +P_pk.toFixed(1), P_cont_kW: +P_cont.toFixed(1) };
    scenarios.forEach(sc => {
      const gr  = Math.atan(sc.grade_pct / 100);
      const r   = rlSteadyPower(v, params, { grade_rad: gr, v_wind: sc.v_wind });
      const P_w = r.P_wheel_kW || 0;
      row['load_' + sc.key] = P_pk > 0.1 ? +Math.min(120, P_w / P_pk * 100).toFixed(1) : 0;
      row['dP_'  + sc.key]  = +Math.max(0, P_pk - P_w).toFixed(1);
      row['P_'   + sc.key]  = +r.P_batt_kW.toFixed(2);
    });
    return row;
  });
}

// ── simRoadLoad ──
// [Fix-①] grades_deg → grades_pct: rlBuildR1/rlCalcVmax와 동일하게 Math.atan(gd/100) 사용
//   수정 전: gd * π/180  → gd=3 → 0.0524 rad (≈5.2% 구배, 과대)
//   수정 후: atan(gd/100) → gd=3 → 0.0300 rad (= 3.0% 구배, 정확)
/**
 * 다중 경사도 조건에서 도로 부하(F, P_kW)를 속도별로 계산한다.
 * 정속 0~300 km/h (2 km/h 간격), grades_pct 배열의 각 경사도별 열 포함.
 *
 * @param {object} params       차량 파라미터
 * @param {number[]} [grades_pct=[0,3,5,8]] 경사도 배열 [%]
 * @returns {object[]}  각 속도 지점 { v, F_total, F_0%, F_3%, P_kW_0%, ... }
 */
function simRoadLoad(params, grades_pct) {
  const data = [];
  for (let v_kmh = 0; v_kmh <= 300; v_kmh += 2) {
    const v = v_kmh / 3.6;
    const entry = { v_kmh };
    grades_pct.forEach((gd) => {
      const gr = Math.atan(gd / 100);   // [Fix-①] atan(%) — rlBuildR1과 동일
      const res = resistanceForcesV3(v, params, gr, params.T_amb, params.T_amb, params.T_amb);
      entry[`F_${gd}%`] = +res.F_total.toFixed(1);
      entry[`P_${gd}%`] = +((res.F_total * v) / 1000).toFixed(2);
    });
    const omega_m = (v < 0.3 ? 0.3 : v) / params.r_wheel * params.gear_ratio;
    const T_m = motorTorqueSpeed(omega_m, 1.0, params, params.T_amb, params.T_amb, params.T_amb);
    entry.F_drive_max = +(T_m * params.gear_ratio * params.eta_driveline / params.r_wheel).toFixed(1);
    // Steady-state efficiency at grade=0
    if (v >= 5) {
      const gr0 = 0;
      const res0 = resistanceForcesV3(v, params, gr0, params.T_amb, params.T_amb, params.T_amb);
      const T_ss = (res0.F_total * params.r_wheel) / (params.gear_ratio * params.eta_driveline);
      if (T_ss > 0 && T_ss < params.T_max_motor) {
        const p = params.poles / 2;
        const Iq_ss = T_ss / (1.5 * p * params.lambda_pm);
        const chain = computeEnergyChain(v, omega_m, T_ss, 0, Iq_ss, params.soc_init, params, gr0, null);
        entry.eta_total_ss = +chain.eta_total;
        entry.P_batt_ss = +chain.P_batt_chem;
        entry.kWh_100km = v > 1 ? +(chain.P_batt_chem / (v * 3.6) * 100).toFixed(1) : 0;
      }
    }
    // Hill climb limit speed (F_drive_max = F_res)
    grades_pct.forEach((gd) => {
      if (gd <= 0) return;
      const gr = Math.atan(gd / 100);   // [Fix-①] atan(%) — rlBuildR1과 동일
      const res = resistanceForcesV3(v, params, gr, params.T_amb, params.T_amb, params.T_amb);
      if (Math.abs(entry.F_drive_max - res.F_total) < 50 && v > 1) {
        entry[`v_lim_${gd}%`] = v_kmh;
      }
    });
    data.push(entry);
  }
  return data;
}

// ── initVehiclePhysicsState ──
/**
 * 차량 물리 시뮬레이션의 초기 상태 객체를 생성한다.
 *
 * 반환 객체는 stepVehiclePhysics의 첫 번째 인수로 전달된다.
 * 포함 항목:
 *   · v: 초기 속도 [m/s]
 *   · SOC: 초기 배터리 충전 상태
 *   · T_batt, T_motor, T_inv: 초기 열 상태 (T_amb 기준)
 *   · 2RC 배터리 등가회로 초기값 (V1, V2)
 *   · 기어 상태 (multi-gear 모드 시)
 *   · ABS/TCS/ESC/칼만 필터 상태
 *
 * @param {object} params    차량 파라미터 (soc_init, T_amb 등)
 * @param {number} [v_init_ms=0]  초기 속도 [m/s]
 * @returns {object}         시뮬레이션 초기 상태 객체
 */
function initVehiclePhysicsState(params, v_init_ms = 0) {
  return {
    v:          v_init_ms,             // 속도 [m/s]
    s:          0,                     // 이동 거리 [m]
    a:          0,                     // 가속도 [m/s²]
    t:          0,                     // 경과 시간 [s]
    soc:        params.soc_init,       // 배터리 SOC [0-1]
    Id:         0,                     // d축 전류 [A]
    Iq:         0,                     // q축 전류 [A]
    foc_int_d:  0,                     // FOC d축 적분기
    foc_int_q:  0,                     // FOC q축 적분기
    a_prev:     0,                     // 이전 가속도 (μ 추정용)
    T_gear:     params.gear_T_oil ?? 80,  // 기어오일 온도 (°C) [P2-B] ?? 수정: gear_T_oil=0 (0°C) 시 80으로 오폭하던 문제 수정
    // Thermal
    thState: {
      T_batt:  params.T_amb, T_inv:   params.T_amb,
      T_motor: params.T_amb, T_cool:  params.T_amb,
    },
    // 서브모델 상태 (null = 비활성)
    battery_2rc:    params.battery_model === '2RC' ? init2RCBatteryState(params) : null,
    tire_state:     params.tire_pacejka_enable      ? initTireState(params)       : null,
    soh_state:      params.soh_enable               ? initSOHState(params)        : null,
    sensorless_state: params.sensorless_enable      ? initSensorlessState(params) : null,
    sei_state:      params.sei_enable               ? initSEIState(params)        : null,
    esc_state:      initESCState(),
    tv_state:       initTorqueVectoringState(),
    lateral_state:  { beta: 0, psi_dot: 0, ay: 0 },
    // [R-06-D] 서스펜션 상태 초기화 (suspension_enable 게이팅)
    susp_state:     params.suspension_enable ? initSuspensionState(params) : null,
    rls_state:      initRLSMuState(params),
    cool_state:     initCoolantFlowState(params),
    mu_hat:         params.mu_ref_nominal || 1.0,
    // 누산기
    E_batt:     0, E_wheel: 0, E_loss: 0,
    Q_b_acc:    0, Q_i_acc:  0, Q_m_acc: 0,
    th_accum:   0,
    // 저크 분석용 (Phase 4)
    a_history:  [],
    bms_state:  params.bms_detailed_enable ? null : null,  // BMS 상태 (시뮬별 초기화)
  };
}

// ── stepVehiclePhysics ──
/**
 * 차량 물리 상태를 dt 시간만큼 진행시킨다 (시뮬레이션 코어 스텝).
 *
 * 처리 순서:
 *   1) 모터 토크·전력 계산 (FOC 전류 제어 또는 1차지연 근사)
 *   2) 회생제동 토크·에너지 체인 계산
 *   3) 발진 제어(Launch Control) PI 슬립 피드백 (lc_enable 시)
 *   4) 도로 저항력 계산 (resistanceForcesV3)
 *   5) 차량 가속도 적분 → v 업데이트 (뉴턴 2법칙)
 *   6) 배터리 SOC/전압/열 업데이트 (socUpdate + thermalStep)
 *   7) 타이어 모델 (슬립·ABS·TCS)
 *   8) 승차감 저크 계산
 *   9) 각종 진단 필드 집계 및 반환
 *
 * @param {object} state   이전 스텝의 차량 상태 (initVehiclePhysicsState로 초기화)
 * @param {object} inputs  현재 스텝 제어 입력 { pedal_pos, brake_demand, target_v_ms, ... }
 * @param {object} params  차량 파라미터
 * @param {number} dt      물리 시뮬레이션 시간 스텝 [s] (보통 0.1s)
 * @param {number} dt_th   열 시뮬레이션 시간 스텝 [s] (보통 1.0s)
 * @returns {{ next_state, out }}
 *   next_state: 다음 스텝 입력으로 쓰일 업데이트된 상태
 *   out: 이번 스텝의 출력 데이터 (v_kmh, P_elec, SOC, T_motor, η, ... )
 */
function stepVehiclePhysics(state, inputs, params, dt, dt_th) {
  const {
    v, s, soc, Id, Iq, a_prev, T_gear,
    thState, battery_2rc, soh_state, sei_state,
    esc_state, tv_state, lateral_state, susp_state,
    rls_state, cool_state, mu_hat,
    E_batt, E_wheel, E_loss,
    Q_b_acc, Q_i_acc, Q_m_acc, th_accum,
    foc_int_d, foc_int_q,
    bms_state,
  } = state;

  const { throttle = 0, brake_demand = 0, grade_rad = 0 } = inputs;
  const m_eff = effectiveMass(params);
  const mode_throttle = inputs.mode_throttle !== undefined
    ? inputs.mode_throttle
    : driveModeThrottle(throttle, params);

  // ── 1. 모터 각속도 / 전기 각속도 ─────────────────────────────────────
  const omega_w = Math.max(v, 0) / params.r_wheel;
  const omega_m = omega_w * params.gear_ratio;
  const omega_e = omega_m * (params.poles / 2);
  const lpm     = lambda_pm_T(thState.T_motor, params);

  // ── 2. MTPV/FW 전류 레퍼런스 ─────────────────────────────────────────
  const V_dc_eff = params.V_dc || 400;
  const curRef   = computeCurrentReferenceWithMTPV(omega_e, params.T_max_motor, V_dc_eff, params, thState.T_motor);

  // ── 3. 구동 토크 (TCS, ESC, TV 포함) ────────────────────────────────
  const T_mot_raw   = motorTorqueSpeed(omega_m, mode_throttle, params, thState.T_motor, thState.T_inv, thState.T_batt);
  const v_wheel_fwd = omega_w * params.r_wheel;
  const slip_est    = v > 1 ? (v_wheel_fwd - v) / Math.max(v, 0.1) : 0;
  const tcs_result  = tcsIntervention(slip_est, T_mot_raw, params);
  const T_mot_tcs   = tcs_result.T_tcs;

  // 횡방향 동역학 (서스펜션 Fz 피드백보다 먼저 초기화 — use-before-declare 버그 수정)
  let new_lateral_state = lateral_state;
  let Fz_front_dyn = null, Fz_rear_dyn = null;

  // [R-06-D] 서스펜션 → Fz 갱신 (lateral_state 초기화 후 실행)
  let new_susp_state = susp_state;
  let camber_f_rad = 0, camber_r_rad = 0;
  if (params.suspension_enable) {
    const z_road = params.road_roughness_enable
      ? (params.IRI || 2.5) * 0.0001 * Math.sin(2 * Math.PI * v * (state.t || 0) / 8)
      : 0;
    new_susp_state = suspensionStep(susp_state || initSuspensionState(params),
      z_road, v, a_prev, new_lateral_state.ay || 0, params, dt);
    Fz_front_dyn = (new_susp_state.Fz_FL + new_susp_state.Fz_FR);
    Fz_rear_dyn  = (new_susp_state.Fz_RL + new_susp_state.Fz_RR);
    // [Fix-Camber] 캠버각 전달 (fourWheelSuspensionStep 결과에서 가져옴)
    camber_f_rad = new_susp_state.camber_f_rad || 0;
    camber_r_rad = new_susp_state.camber_r_rad || 0;

    // [Fix-Pitch] 피치 동역학 → dFz_lon 연동
    //   서스펜션에서 계산된 pitch_angle_rad로 동적 하중이동 보정
    //   Fz_front_pitch = -m × ax_pitch × h / L   (ax_pitch = g × sin(pitch))
    if (new_susp_state.pitch_angle_rad !== undefined) {
      const m_p  = params.m      || 1850;
      const h_p  = params.h_cg   || 0.54;
      const L_p  = (params.l_f || 1.4) + (params.l_r || 1.475);
      const g_p  = G_GRAVITY;
      // 피치 각도로 인한 추가 종방향 하중이동
      const ax_pitch = g_p * Math.sin(new_susp_state.pitch_angle_rad);
      const dFz_pitch = m_p * ax_pitch * h_p / L_p;
      Fz_front_dyn = Math.max(100, Fz_front_dyn - dFz_pitch);
      Fz_rear_dyn  = Math.max(100, Fz_rear_dyn  + dFz_pitch);
    }
  }
  if (params.lateral_enable) {
    new_lateral_state = lateralDynamicsStep(
      { ...lateral_state, camber_f_rad, camber_r_rad },
      params.delta_steer_rad || 0,
      Math.max(v, 0.1), Fz_front_dyn, Fz_rear_dyn, params, dt
    );
  }

  // ESC 개입
  const psi_ref_esc  = computeYawRateRef_v4(params.delta_steer_rad || 0, v, mu_hat, params).psi_ref;
  const new_esc_state = { ...esc_state };
  const esc_result   = escIntervention(new_lateral_state.psi_dot || 0, psi_ref_esc, v, params, new_esc_state, dt);
  const T_mot_esc    = esc_result.active ? T_mot_tcs * (esc_result.torque_cut || 1.0) : T_mot_tcs;

  // 토크 벡터링 (AWD)
  // [Fix-TV-Slip] 4륜 슬립 단일화(slip_est) → 운동학 기반 차축·좌우 독립 계산
  //   전륜: v_wheel_f = v + yaw_rate × l_f  (요 운동으로 전축이 더 빠름)
  //   후륜: v_wheel_r = v - yaw_rate × l_r
  //   좌/우: ±yaw_rate × (track/2)
  const psi_tv   = new_lateral_state.psi_dot || 0;
  const lf_tv    = params.l_f  || 1.4;
  const lr_tv    = params.l_r  || 1.475;
  const tf_tv    = (params.track_front || params.track_width || 1.58) / 2;
  const tr_tv    = (params.track_rear  || params.track_width || 1.58) / 2;
  const v_safe   = Math.max(v, 0.1);
  // 각 코너 접지 속도 (자전거 모델 1차 근사)
  const v_FL = v_safe + psi_tv * lf_tv - psi_tv * tf_tv;
  const v_FR = v_safe + psi_tv * lf_tv + psi_tv * tf_tv;
  const v_RL = v_safe - psi_tv * lr_tv - psi_tv * tr_tv;
  const v_RR = v_safe - psi_tv * lr_tv + psi_tv * tr_tv;
  // 슬립률 = (v_wheel - v_ground) / v_ground (구동: 양수)
  const slipCorner = (v_w) => v > 1 ? (omega_w * params.r_wheel - v_w) / Math.max(v_w, 0.1) : 0;
  const slip_FL_c = slipCorner(v_FL);
  const slip_FR_c = slipCorner(v_FR);
  const slip_RL_c = slipCorner(v_RL);
  const slip_RR_c = slipCorner(v_RR);

  const vs_tv = {
    v, ax: a_prev, ay: new_lateral_state.ay || 0,
    yaw_rate: psi_tv,
    delta_steer: params.delta_steer_rad || 0,
    mu_hat,
    slip_FL: slip_FL_c, slip_FR: slip_FR_c,
    slip_RL: slip_RL_c, slip_RR: slip_RR_c,
  };
  const new_tv_state = { ...tv_state };
  // [A21-15-03] TV 삼항 데드코드 수정 (stepVehiclePhysics 패리티)
  // [BugFix-A04-03] awd_calib_enable=true 시에도 torqueVectoring 진입하도록 조건 확장
  const tv_result = ((params.awd_calib_enable || params.torque_vectoring_enable) && params.awd_enable)
    ? torqueVectoring(T_mot_esc, vs_tv, params, new_tv_state)
    : { T_front: 0, T_rear: T_mot_esc, awd_active: params.awd_enable || false };
  // [A21-15-04] AWD 시 전·후륜 토크 합산 (stepVehiclePhysics 패리티)
  const T_mot_awd = params.awd_enable
    ? (((tv_result.T_front || 0) + (tv_result.T_rear || 0)) || T_mot_esc)
    : T_mot_esc;
  // [A10] BMS 콘택터 차단 → 구동 토크 강제 0
  const T_mot     = (bms_state && bms_state.contactor_on === false) ? 0 : T_mot_awd;

  // ── 4. 저항력 ─────────────────────────────────────────────────────────
  const res     = resistanceForcesV3(v, params, grade_rad, params.T_amb, thState.T_cool, thState.T_motor);
  const F_normal = params.m * g * Math.cos(grade_rad);

  // ── 5. RLS μ 추정 ──────────────────────────────────────────────────────
  const F_drv_est   = T_mot * params.gear_ratio * params.eta_driveline / params.r_wheel;
  const new_rls_state = rlsMuUpdate({ ...rls_state }, F_drv_est, F_normal, a_prev, params.m, params);
  const new_mu_hat   = new_rls_state.mu_est;

  // ── 6. 회생/구동력 분기 ──────────────────────────────────────────────
  const is_regen = mode_throttle < (params.regen_threshold || 0.02) && v > 0.5;
  let F_drv, regen_result = null;
  if (is_regen) {
    // [BugFix-A04-02] regen_calib_enable 시 regenMapDemand LUT 우선 적용
    const bd_base = Math.min(1.0, (1.0 - mode_throttle) * driveModeRegenStrength(params));
    const bd = params.regen_calib_enable
      ? Math.max(regenMapDemand(v, 0, params), bd_base)
      : bd_base;
    regen_result = regenTorqueEnhanced(omega_m, bd, params, soc, thState.T_batt, thState.T_motor, thState.T_inv);
    const blend  = regenFrictionBlend(regen_result.T_regen, regen_result.T_friction, v, soc, omega_m, params, dt);
    regen_result.T_regen    = blend.T_regen;
    regen_result.T_friction = blend.T_friction;
    regen_result._blend     = blend;
    // [A21-18-02] B-02 수정: 회생 방향 에너지 흐름 = 바퀴→모터, ÷η가 올바름
    // 구동(×η): P_wheel = P_motor × η  →  F = T·gr·η/r
    // 회생(÷η): P_motor = P_wheel × η  →  F = T·gr / (r·η)
    F_drv = -(regen_result.T_regen + regen_result.T_friction) * params.gear_ratio / (params.r_wheel * params.eta_driveline);
  } else {
    F_drv = T_mot * params.gear_ratio * params.eta_driveline / params.r_wheel;
    F_drv = Math.min(F_drv, new_rls_state.mu_est * params.m * g * Math.cos(grade_rad));
  }

  // ── 7. dq 전류 제어 ─────────────────────────────────────────────────
  let Iq_ref, Id_ref;
  if (is_regen && regen_result) {
    Iq_ref = -(regen_result.T_regen / Math.max(0.01, 1.5 * (params.poles / 2) * lpm));
    Id_ref = 0;
  } else {
    Iq_ref = curRef.Iq || (T_mot > 0 ? T_mot / (1.5 * (params.poles / 2) * lpm) : 0);
    Id_ref = curRef.Id || 0;
  }

  let new_Id = Id, new_Iq = Iq;
  let new_foc_int_d = foc_int_d, new_foc_int_q = foc_int_q;

  if (params.foc_current_pi_enable) {
    const e_d = Id_ref - new_Id, e_q = Iq_ref - new_Iq;
    new_foc_int_d += e_d * dt;
    new_foc_int_q += e_q * dt;
    const V_max_foc = V_dc_eff * (params.fw_v_margin || 0.95) / Math.sqrt(3);
    const int_limit = V_max_foc / Math.max(params.Ki_id, 1);
    new_foc_int_d = Math.max(-int_limit, Math.min(int_limit, new_foc_int_d));
    new_foc_int_q = Math.max(-int_limit, Math.min(int_limit, new_foc_int_q));
    let Vd_cmd = params.Kp_id * e_d + params.Ki_id * new_foc_int_d - omega_e * (params.Lq || 0.0006) * new_Iq;
    let Vq_cmd = params.Kp_iq * e_q + params.Ki_iq * new_foc_int_q + omega_e * ((params.Ld || 0.0003) * new_Id + lpm);
    const V_mag = Math.sqrt(Vd_cmd * Vd_cmd + Vq_cmd * Vq_cmd);
    if (V_mag > V_max_foc) {
      const vsc = V_max_foc / V_mag;
      Vd_cmd *= vsc; Vq_cmd *= vsc;
      new_foc_int_d -= e_d * dt;
      new_foc_int_q -= e_q * dt;
    }
    const Rs_eff = Rs_T(thState.T_motor, params);
    const dId_dt = (Vd_cmd - Rs_eff * new_Id + omega_e * (params.Lq || 0.0006) * new_Iq) / (params.Ld || 0.0003);
    const dIq_dt = (Vq_cmd - Rs_eff * new_Iq - omega_e * ((params.Ld || 0.0003) * new_Id + lpm)) / (params.Lq || 0.0006);
    new_Id += dId_dt * dt;
    new_Iq += dIq_dt * dt;
    const I_mag_pi = Math.sqrt(new_Id * new_Id + new_Iq * new_Iq);
    const I_max_pi = I_max_T(thState.T_inv, params) || params.I_max_rms;
    if (I_mag_pi > I_max_pi) { const sc = I_max_pi / I_mag_pi; new_Id *= sc; new_Iq *= sc; }
  } else {
    const tau_d  = (params.Ld || 0.0003) / (params.Rs || 0.015);
    const tau_q  = (params.Lq || 0.0006) / (params.Rs || 0.015);
    const alpha_d = Math.min(0.99, dt / (tau_d + dt));
    const alpha_q = Math.min(0.99, dt / (tau_q + dt));
    new_Id = new_Id + alpha_d * (Id_ref - new_Id);
    new_Iq = new_Iq + alpha_q * (Iq_ref - new_Iq);
    const I_mag_dq  = Math.sqrt(new_Id * new_Id + new_Iq * new_Iq);
    const I_max_eff = I_max_T(thState.T_inv, params) || params.I_max_rms;
    if (I_mag_dq > I_max_eff) { const sc = I_max_eff / I_mag_dq; new_Id *= sc; new_Iq *= sc; }
  }

  const steinmetz = steinmetzIronLoss_v3(omega_e, params);

  // ── 8. 운동방정식 ───────────────────────────────────────────────────
  const a_new = (F_drv - res.F_total) / m_eff;
  const v_new = Math.max(0, v + a_new * dt);
  const s_new = s + v_new * dt;

  // ── 9. 에너지 체인 ──────────────────────────────────────────────────
  let chain, P_elec;
  if (is_regen && regen_result) {
    chain  = computeRegenChain(omega_m, regen_result.T_regen, new_Id, new_Iq, soc, params, thState.T_batt, thState.T_motor);
    P_elec = -Math.max(0, (chain.P_batt_charge || 0) * 1000);
  } else {
    chain  = computeEnergyChain(v, omega_m, T_mot, new_Id, new_Iq, soc, params, grade_rad, thState);
    P_elec = chain.P_batt_chem * 1000;
  }

  // ── 10. 2RC 배터리 모델 ─────────────────────────────────────────────
  let new_battery_2rc = battery_2rc;
  if (battery_2rc && params.battery_model === '2RC') {
    const I_batt_est = P_elec / Math.max(1, V_dc_eff);
    battery2RCModelWithSEI(battery_2rc, I_batt_est, dt, params, null);
    new_battery_2rc = { ...battery_2rc, SOC: soc + (-P_elec * dt / (params.batt_cap * 3600 * params.V_nom)) };
    new_battery_2rc.T_cell = thState.T_batt;
  }

  // ── 11. SOC 업데이트 ─────────────────────────────────────────────────
  const new_soc = socUpdate(soc, P_elec, dt, params);
  const I_batt_step = Math.abs(P_elec) / Math.max(1, V_dc_eff);

  // ── 12. SOH 업데이트 ──────────────────────────────────────────────────
  let new_soh_state = soh_state;
  if (soh_state && params.soh_enable) {
    const batt_snap = { SOC: new_soc, T_cell: thState.T_batt };
    new_soh_state = updateSOH(soh_state, batt_snap, I_batt_step, dt, params);
  }

  // ── 13. SEI 성장 ──────────────────────────────────────────────────────
  let new_sei_state = sei_state;
  if (sei_state) {
    const batt_v7 = { T_cell: thState.T_batt, V_terminal: V_dc_eff };
    new_sei_state = updateSEI(sei_state, batt_v7, I_batt_step, dt, params);
  }

  // ── 14. 기어박스 온도 ─────────────────────────────────────────────────
  const gear_loss  = gearLossTempEnhanced(omega_m, T_gear, T_mot, params); // [R-06-A] unified
  const new_T_gear = gearThermalStep(T_gear, gear_loss.P_loss, v, params, dt);

  // ── 15. 냉각 유량 제어 ────────────────────────────────────────────────
  let new_cool_state = cool_state;
  const new_th_accum = th_accum + dt;
  let new_Q_b_acc = Q_b_acc + (chain.Q_batt || 0) * dt;
  let new_Q_i_acc = Q_i_acc + (chain.Q_inv  || 0) * dt;
  let new_Q_m_acc = Q_m_acc + (chain.Q_motor || 0) * dt;

  // ── 16. 열 모델 업데이트 (dt_th 마다) ────────────────────────────────
  let new_thState = thState;
  let reset_th_accum = new_th_accum;
  let reset_Qb = new_Q_b_acc, reset_Qi = new_Q_i_acc, reset_Qm = new_Q_m_acc;
  if (new_th_accum >= dt_th) {
    new_cool_state = coolantFlowControl(cool_state, thState.T_cool, params, new_th_accum);
    new_thState = thermalStep(thState, new_Q_b_acc / new_th_accum, new_Q_i_acc / new_th_accum,
                              new_Q_m_acc / new_th_accum, v_new, params, new_th_accum,
                              new_cool_state.phi_flow); // [A25-Fix3] phi_flow 전달
    reset_th_accum = 0; reset_Qb = 0; reset_Qi = 0; reset_Qm = 0;
  }

  // ── 17. 에너지 누산 ───────────────────────────────────────────────────
  const new_E_batt  = is_regen ? E_batt - P_elec * dt : E_batt + P_elec * dt;
  const new_E_wheel = is_regen ? E_wheel : E_wheel + (chain.P_wheel || 0) * 1000 * dt;
  const new_E_loss  = E_loss + ((chain.P_loss_batt || 0) + (chain.P_loss_inv || 0) +
                                (chain.P_loss_motor || 0) + (chain.P_loss_gear || 0)) * 1000 * dt;

  return {
    // 기본 운동 상태
    v: v_new, s: s_new, a: a_new, t: state.t + dt,
    soc: new_soc,
    Id: new_Id, Iq: new_Iq,
    foc_int_d: new_foc_int_d, foc_int_q: new_foc_int_q,
    a_prev: a_new,
    T_gear: new_T_gear,
    thState: new_thState,
    // 서브모델
    battery_2rc: new_battery_2rc,
    soh_state:   new_soh_state,
    sei_state:   new_sei_state,
    esc_state:   new_esc_state,
    tv_state:    new_tv_state,
    lateral_state: new_lateral_state,
    susp_state:  new_susp_state,
    bms_state:   bms_state,
    rls_state:   new_rls_state,
    cool_state:  new_cool_state,
    mu_hat:      new_mu_hat,
    a_history:   (state.a_history || []),
    // 누산기
    E_batt:   new_E_batt,
    E_wheel:  new_E_wheel,
    E_loss:   new_E_loss,
    Q_b_acc:  reset_Qb,
    Q_i_acc:  reset_Qi,
    Q_m_acc:  reset_Qm,
    th_accum: reset_th_accum,
    // 계산 결과 (로깅용)
    _chain:        chain,
    _res:          res,
    _tcs_result:   tcs_result,
    _esc_result:   esc_result,
    _tv_result:    tv_result,
    _regen_result: regen_result,
    _steinmetz:    steinmetz,
    _gear_loss:    gear_loss,
    _P_elec:       P_elec,
    _is_regen:     is_regen,
    _T_mot:        T_mot,
    _omega_m:      omega_m,
    _curRef:       curRef,
    _I_batt_step:  I_batt_step,
  };
}

// ── variableCdA ──
/**
 * 속도·그릴 셔터 상태·요각에 따른 유효 CdA를 계산한다.
 * 셔터 활성화 시: 저속 또는 모터 고온(≥T_motor_open)이면 셔터 개방(CdA_open).
 * 속도 의존 증가: CdA_v3 = CdA_shutter × (1 + k_s × v²).
 * [Fix-YawCdA] 요각 β 의존 보정: CdA × (1 + k_yaw × sin²β).
 *
 * @param {number} v         차속 [m/s]
 * @param {object} params    CdA, grille_shutter_enable, CdA_speed_coeff, vehicle_yaw_rad 등
 * @param {number} [T_motor] 모터 온도 [°C] (셔터 로직에서 사용)
 * @returns {{ CdA_v3: number, shutter_open: boolean|null, CdA_base: number, CdA_speed_add: number }}
 */
function variableCdA(v, params, T_motor) {
  const CdA_base = params.CdA || 0.49;
  let CdA_shutter;
  let shutter_open = null;
  if (!params.grille_shutter_enable) {
    const k_s = params.CdA_speed_coeff || 0.0003;
    CdA_shutter = CdA_base;
    // [Fix-YawCdA] 아래 요각 보정으로 계속 진행
  } else {
    // Shutter state: open if low speed OR high motor temp
    const v_kph = v * 3.6;
    shutter_open = v_kph < (params.grille_open_speed_kph || 80)
      || (T_motor !== undefined && T_motor > (params.grille_open_T_motor || 100));
    CdA_shutter = shutter_open ? (params.CdA_open || 0.52) : (params.CdA_closed || 0.46);
  }
  // Speed-dependent augmentation
  const k_s = params.CdA_speed_coeff || 0.0003;
  let CdA_v3 = CdA_shutter * (1 + k_s * v * v);

  // [Fix-YawCdA] 차량 요각(yaw angle) 의존 CdA 보정
  // 코너링 시 차체 측면 투영 면적 증가 → CdA +5~10% (yaw 10°에서 약 +7%)
  // CdA_yaw = CdA_v3 × (1 + k_yaw × sin²(β))
  //   β = yaw angle (차량 진행방향 vs 차속 벡터 편차), k_yaw ≈ 0.50 (측면면적 기여)
  // params.vehicle_yaw_rad: 동역학 모듈이 계산한 요각 (없으면 0 — 기존 동작 유지)
  if (params.vehicle_yaw_rad !== undefined && params.yaw_cda_enable !== false) {
    const beta     = params.vehicle_yaw_rad;
    const k_yaw    = params.CdA_yaw_coeff || 0.50;
    const sin_b    = Math.sin(beta);
    const delta_yaw = k_yaw * sin_b * sin_b;
    CdA_v3 *= (1 + delta_yaw);
  }

  return {
    CdA_v3,
    shutter_open,
    CdA_base: CdA_shutter,
    CdA_speed_add: CdA_shutter * k_s * v * v,
  };
}

// ── gearLossTempEnhanced ──
/**
 * 기어박스 온도 의존 손실 전력을 계산한다.
 * gear_loss_temp_enable=false 이면 고정 eta_driveline 폴백.
 * 점성 손실: (k0·k_cold + k_visc·(T_ref−T_gear)·ω), 메시 손실: k2·ω² + k_mesh·|T_out|,
 * 베어링 손실: k_bearing·ω² + 0.0003·|T_out|·ω. [Fix-M2] 분모 0 방지 클램프.
 *
 * @param {number} omega    기어 입력 각속도 [rad/s]
 * @param {number} T_gear   기어 오일 온도 [°C]
 * @param {number} T_output 출력 토크 [N·m]
 * @param {object} params   gear_loss_temp_enable, gear_k0, gear_k2, gear_k_viscosity 등
 * @returns {{ P_loss: number, eta_gear: number, P_viscosity: number, P_mesh: number, P_bearing: number }}
 */
function gearLossTempEnhanced(omega, T_gear, T_output, params) {
  const K_gear = params.K_gear || 1.0; // [A21] 기어 손실 보정 계수
  if (!params.gear_loss_temp_enable) {
    // 고정 eta 폴백
    const P_mech = Math.abs(T_output * omega) + 1;
    const P_loss = P_mech * (1 - (params.eta_driveline || 0.97)) * K_gear;
    return { P_loss: +P_loss.toFixed(1), eta_gear: +(params.eta_driveline || 0.97),
             P_viscosity: P_loss * 0.5, P_mesh: P_loss * 0.3, P_bearing: P_loss * 0.2 };
  }
  const k0 = params.gear_k0 || 50;
  const k2 = params.gear_k2 || 1.5e-5;
  const k_cold = T_gear < 40 ? 1 + 0.015 * (40 - T_gear) : 1.0;
  const T_ref  = params.gear_T_ref || 80;
  const k_visc = params.gear_k_viscosity || 0.0015;
  const P_viscosity = (k0 * k_cold + k_visc * Math.max(0, T_ref - T_gear) * omega) * K_gear;
  const P_mesh      = (k2 * omega * omega + (params.gear_k_mesh || 0.0012) * Math.abs(T_output)) * K_gear;
  const P_bearing   = ((params.gear_k_bearing || 0.00008) * omega * omega + 0.0003 * Math.abs(T_output) * omega) * K_gear;
  const P_loss      = P_viscosity + P_mesh + P_bearing;
  const P_output    = Math.abs(T_output * omega) + 1;
  // [Fix-M2] P_output + P_loss 분모 0 방지 — P_loss가 비정상적으로 큰 경우 NaN 차단
  const P_denom     = P_output + P_loss;
  const eta_gear    = Math.max(0.85, Math.min(0.99, isFinite(P_denom) && P_denom > 1e-6 ? P_output / P_denom : 0.92));
  return { P_loss: +P_loss.toFixed(1), eta_gear: +eta_gear.toFixed(4), P_viscosity, P_mesh, P_bearing };
}

// ── initGearState ──
/**
 * 다단 변속기 상태 객체를 초기화한다.
 *
 * @param {object} params  (현재 미사용 — 향후 확장 대비)
 * @returns {{ current_gear: number, is_shifting: boolean, shift_timer: number, shift_count: number, last_shift_t: number }}
 */
function initGearState(params) {
  return {
    current_gear:  1,          // 현재 단수 (1-indexed)
    is_shifting:   false,      // 변속 중 플래그
    shift_timer:   0,          // 변속 중 경과 시간 [s]
    shift_count:   0,          // 총 변속 횟수
    last_shift_t:  -5,         // 마지막 변속 시각 (히스테리시스)
  };
}

// ── getActiveGear ──
/**
 * 현재 속도·토크·SOC에 따라 적정 변속단을 선택하고 기어 상태를 갱신한다.
 * multi_gear_enable=false 이면 단일 고정 기어비 반환.
 * [P-11-Fix] 단수별 독립 업/다운시프트 속도 배열(gear_shift_speeds_up/dn_kmh) 지원;
 * 미설정 시 scalar gear_shift_speed_kmh를 등간격 선형 확장하여 하위호환.
 * 히스테리시스: 변속 후 gear_shift_inhibit_time [s] 동안 재변속 억제.
 *
 * @param {number} v_ms        차속 [m/s]
 * @param {number} T_demand    요구 토크 [N·m]
 * @param {number} soc         배터리 SOC [0-1]
 * @param {object} gear_state  현재 기어 상태 (initGearState 반환값)
 * @param {object} params      multi_gear_enable, gear_ratios, gear_shift_speed_kmh 등
 * @param {number} dt          시간 스텝 [s]
 * @param {number} t           현재 시뮬레이션 시각 [s]
 * @returns {{ gear_ratio_eff: number, gear_state_new: object, torque_scale: number, gear: number }}
 */
function getActiveGear(v_ms, T_demand, soc, gear_state, params, dt, t) {
  if (!params.multi_gear_enable) {
    return { gear_ratio_eff: params.gear_ratio || 9.5, gear_state_new: gear_state, torque_scale: 1.0, gear: 1 };
  }
  const ratios  = params.gear_ratios || [11.5, 6.0];
  const n_gears = ratios.length;

  // [P-11-Fix] 기어수별 독립 업/다운시프트 속도 배열
  // 이전: 단일 scalar → 3단 이상에서 1→2, 2→3, 3→4 모두 동일 속도 기준 적용 (물리 부정확)
  // 수정: gear_shift_speeds_up_kmh[]  / gear_shift_speeds_dn_kmh[] 배열 우선 사용
  //       미설정 시 scalar gear_shift_speed_kmh을 등간격 선형 확장하여 backward 호환 유지
  //
  //  v_ups[i] : i단 → (i+1)단 업시프트 속도 [km/h]  (i = 0-based, 길이 = n_gears-1)
  //  v_dns[i] : (i+1)단 → i단 다운시프트 속도 [km/h] (i = 0-based, 길이 = n_gears-1)
  //
  //  배열 예시 (3단):
  //    gear_shift_speeds_up_kmh: [65, 100, 130]  → 1→2: 65, 2→3: 100
  //    gear_shift_speeds_dn_kmh: [50,  80, 110]  → 2→1: 50, 3→2:  80
  //
  //  scalar fallback 자동 확장 규칙:
  //    v_up_base = gear_shift_speed_kmh || 65
  //    v_ups[i]  = v_up_base + i * (v_up_base * 0.50)   (단마다 50% 가산)
  //    예: 65 → [65, 97.5, 130, ...]
  //    v_dn_base = gear_shift_down_speed_kmh || (v_up_base * 0.77)
  //    v_dns[i]  = v_dn_base + i * (v_up_base * 0.50)
  const _v_up_base = params.gear_shift_speed_kmh      || 65;
  const _v_dn_base = params.gear_shift_down_speed_kmh || (_v_up_base * 0.77);
  const v_ups = params.gear_shift_speeds_up_kmh
    || Array.from({ length: n_gears - 1 }, (_, i) => +(_v_up_base + i * _v_up_base * 0.50).toFixed(1));
  const v_dns = params.gear_shift_speeds_dn_kmh
    || Array.from({ length: n_gears - 1 }, (_, i) => +(_v_dn_base + i * _v_up_base * 0.50).toFixed(1));

  const t_dip    = params.gear_shift_duration_s || 0.25;
  const dip_frac = params.gear_shift_torque_dip || 0.12;
  const hyst_t   = 2.0; // 최소 변속 간격 [s]

  let { current_gear, is_shifting, shift_timer, shift_count, last_shift_t } = gear_state;
  const v_kmh = v_ms * 3.6;

  // 변속 중 처리
  if (is_shifting) {
    shift_timer += dt;
    const torque_scale = 1.0 - dip_frac * Math.sin(Math.PI * Math.min(1, shift_timer / t_dip));
    if (shift_timer >= t_dip) {
      is_shifting = false;
      shift_timer = 0;
    }
    return {
      gear_ratio_eff: ratios[current_gear - 1],
      gear_state_new: { current_gear, is_shifting, shift_timer, shift_count, last_shift_t },
      torque_scale: Math.max(0.1, torque_scale),
      gear: current_gear,
    };
  }

  // [P-11-Fix] 업/다운시프트: 현재 기어에 대응하는 속도 임계값 참조
  //   v_ups[current_gear - 1] : current → current+1 업시프트 기준 속도
  //   v_dns[current_gear - 2] : current → current-1 다운시프트 기준 속도
  const v_up_cur = v_ups[current_gear - 1] ?? v_ups[v_ups.length - 1]; // 배열 범위 초과 방어
  const v_dn_cur = v_dns[current_gear - 2] ?? v_dns[0];

  let target_gear = current_gear;
  // 업시프트 조건
  if (current_gear < n_gears && v_kmh > v_up_cur && (t - last_shift_t) > hyst_t) {
    target_gear = current_gear + 1;
  }
  // 다운시프트 조건: v < 기어별 다운시프트 속도 OR 토크 요구 높을 때
  else if (current_gear > 1 && (v_kmh < v_dn_cur || T_demand > (params.T_max_motor||400)*0.85) && (t - last_shift_t) > hyst_t) {
    target_gear = current_gear - 1;
  }
  // SOC 낮을 때 고단(효율) 우선 — [Fix-INFO-3] hyst_t 쿨다운 유지
  if (soc < (params.gear_shift_soc_boost || 0.7) && current_gear < n_gears
      && v_kmh > v_up_cur * 0.8 && (t - last_shift_t) > hyst_t) {
    target_gear = Math.min(n_gears, current_gear + 1);
  }

  if (target_gear !== current_gear) {
    current_gear = target_gear;
    is_shifting  = true;
    shift_timer  = 0;
    shift_count += 1;
    last_shift_t = t;
  }

  return {
    gear_ratio_eff: ratios[current_gear - 1],
    gear_state_new: { current_gear, is_shifting, shift_timer, shift_count, last_shift_t },
    torque_scale: 1.0,
    gear: current_gear,
  };
}

// ── computeEnergyChain ──
/**
 * 정속 주행 시의 에너지 체인 전체 손실을 분해하여 계산한다.
 *
 * 에너지 흐름 (구동 방향):
 *   배터리(P_batt_chem) → 인버터(−P_loss_inv) → 모터(−P_loss_motor) → 기어(−P_loss_gear) → 바퀴(P_wheel)
 *   + 도로 저항력(F_aero, F_roll, F_grade)
 *
 * 손실 분해:
 *   P_loss_inv   = 도통손실(V_f·I) + 스위칭 손실(E_on_off·f_sw) + 보조
 *   P_loss_motor = 동손(I²R) + 철손(k_h·f + k_e·f²) + 기계손
 *   P_loss_gear  = k_g0 + k_g1·ω + k_g2·ω²
 *   P_loss_batt  = I²·Ri (내부저항 Joule 손실)
 *
 * 듀얼 모터 시 Cu 손실은 torque_split 제곱 비율로, Fe·기계 손실은 ×2로 스케일.
 *
 * @param {number} v            차속 [m/s]
 * @param {number} omega_motor  모터 각속도 [rad/s]
 * @param {number} T_motor_torque 모터 토크 [Nm]
 * @param {number} Id           d축 전류 [A]
 * @param {number} Iq           q축 전류 [A]
 * @param {number} soc          현재 SOC [0, 1]
 * @param {object} params       차량 파라미터
 * @param {number} [grade_rad=0] 경사각 [rad]
 * @param {object} [thState]    열 상태 { T_motor, T_inv, T_batt, T_gear }
 * @returns {object}            에너지 체인 전 필드 (P_wheel, P_batt_chem, η_total 등)
 */
function computeEnergyChain(v, omega_motor, T_motor_torque, Id, Iq, soc, params, grade_rad, thState) {
  const T_batt = thState ? thState.T_batt : (params.batt_temp || 25);
  const T_inv = thState ? thState.T_inv : (params.batt_temp || 25);
  const T_motor = thState ? thState.T_motor : (params.batt_temp || 25);
  const T_cool = thState ? thState.T_cool : (params.batt_temp || 25);

  const T_wheel = T_motor_torque * params.gear_ratio;
  const omega_wheel = omega_motor / params.gear_ratio;
  // P_wheel (= P_motor_mech): 모터 샤프트 출력 [W]. 기어 손실 계산의 입력 기준으로 사용.
  // 실제 접지면 휠 출력은 기어 손실 계산 후 P_wheel_actual로 별도 산출.
  const P_wheel = T_wheel * omega_wheel;

  // [R-06-A] 기어박스 온도 의존 손실 — gearLossTempEnhanced (통합 canonical)
  const T_gear_chain = thState?.T_gear ?? (params.gear_T_oil ?? 80); // [P2-B] ?? 수정: 0°C 엣지케이스 방어
  // [Fix-GearFrame] omega_motor(모터측)와 T_output을 같은 축 기준으로 통일 — 다른 호출부(gearLossTempEnhanced(omega_m, T_gear, T_mot, ...))와 동일 관례.
  //   수정 전: T_wheel(휠측, ×gear_ratio) 전달 → P_mech=T_output·omega가 기어비배 만큼 부풀려져 eta_gear 과소 산출
  const gear_v7 = gearLossTempEnhanced(omega_motor, T_gear_chain, T_motor_torque, params);
  const gear = { P_loss: gear_v7.P_loss, eta_gear: gear_v7.eta_gear,
                 P_viscosity: gear_v7.P_viscosity, P_bearing: gear_v7.P_bearing, P_mesh: gear_v7.P_mesh };
  const P_motor_mech = T_motor_torque * omega_motor;
  // [Fix-CRIT-2] 실제 접지면 휠 출력: 모터 샤프트 출력에서 기어 기계 손실을 차감
  //   수정 전: P_wheel(= P_motor_mech)을 그대로 사용 → 기어 손실 이전 출력을 "휠 출력"으로 오표기
  //   수정 후: P_wheel_actual = P_motor_mech − gear.P_loss (물리적 접지면 출력)
  //   에너지 흐름: Battery → [배터리손실] → DC → [인버터손실] → AC →
  //               [모터손실] → 모터샤프트(P_motor_mech) → [기어손실] → 휠(P_wheel_actual)
  const P_wheel_actual = Math.max(0, P_motor_mech - gear.P_loss);
  const motor = computeMotorLosses(omega_motor, T_motor_torque, Id, Iq, params, T_motor);
  const I_rms = Math.sqrt(Id * Id + Iq * Iq);
  const inv = computeInverterLosses(I_rms, T_inv, params);

  // [Sprint-2] AWD 전·후륜 독립 모터 손실 모델
  // 기존(A35-BUG-M2): 단일 computeMotorLosses → scale 계수(rf²+rr², 2×) 근사
  // 수정: 전·후 각각 독립 computeMotorLosses() 호출
  //   params.front_motor / rear_motor 없으면 공통 params 폴백 (기존 호환)
  const _is_dual = (params.motor_config === 'dual') ||
                   (params.awd_enable === true &&
                    params.motor_config !== 'single_rear' &&
                    params.motor_config !== 'single_front');
  let _P_cu_eff, _P_fe_eff, _P_pm_eddy_eff, _P_mech_eff, _motor_loss_eff;
  let _P_inv_cond_eff, _P_inv_sw_eff, _P_inv_aux_eff, _inv_loss_eff;
  let _awd_front_loss = null, _awd_rear_loss = null;
  if (_is_dual) {
    const _rf = params.torque_split_front != null ? params.torque_split_front : 0.4;
    const _rr = 1.0 - _rf;
    const _pf = params.front_motor ? { ...params, ...params.front_motor } : params;
    const _pr = params.rear_motor  ? { ...params, ...params.rear_motor  } : params;
    const _Tf = T_motor_torque * _rf, _Idf = Id * _rf, _Iqf = Iq * _rf;
    const _Tr = T_motor_torque * _rr, _Idr = Id * _rr, _Iqr = Iq * _rr;
    const _mf = computeMotorLosses(omega_motor, _Tf, _Idf, _Iqf, _pf, T_motor);
    const _mr = computeMotorLosses(omega_motor, _Tr, _Idr, _Iqr, _pr, T_motor);
    _awd_front_loss = _mf;
    _awd_rear_loss  = _mr;
    _P_cu_eff       = _mf.P_cu      + _mr.P_cu;
    _P_fe_eff       = _mf.P_fe      + _mr.P_fe;
    _P_pm_eddy_eff  = (_mf.P_pm_eddy || 0) + (_mr.P_pm_eddy || 0);
    _P_mech_eff     = _mf.P_mech    + _mr.P_mech;
    _motor_loss_eff = _P_cu_eff + _P_fe_eff + _P_pm_eddy_eff + _P_mech_eff;
    const _Irmsf = Math.sqrt(_Idf*_Idf + _Iqf*_Iqf);
    const _Irmsr = Math.sqrt(_Idr*_Idr + _Iqr*_Iqr);
    const _invf  = computeInverterLosses(_Irmsf, T_inv, _pf);
    const _invr  = computeInverterLosses(_Irmsr, T_inv, _pr);
    _P_inv_cond_eff = _invf.P_cond + _invr.P_cond;
    _P_inv_sw_eff   = _invf.P_sw   + _invr.P_sw;
    _P_inv_aux_eff  = _invf.P_aux  + _invr.P_aux;
    _inv_loss_eff   = _P_inv_cond_eff + _P_inv_sw_eff + _P_inv_aux_eff;
  } else {
    _P_cu_eff       = motor.P_cu;
    _P_fe_eff       = motor.P_fe;
    _P_pm_eddy_eff  = motor.P_pm_eddy || 0;
    _P_mech_eff     = motor.P_mech;
    _motor_loss_eff = motor.P_total;
    _P_inv_cond_eff = inv.P_cond;
    _P_inv_sw_eff   = inv.P_sw;
    _P_inv_aux_eff  = inv.P_aux;
    _inv_loss_eff   = inv.P_total;
  }

  // [Fix-②] P_ac 정의 정정: 에너지 흐름 경계 명확화
  //   올바른 흐름: Battery → Inverter(AC) → Motor shaft(mechanical) → Gearbox → Wheel
  //   P_ac  = 인버터 AC 출력  = 모터 전기입력  = 모터 기계출력 + 모터손실 (전기→기계 변환 경계)
  //   P_dc  = 인버터 DC 입력  = P_ac + 인버터손실
  //   gear.P_loss는 기계계(모터 샤프트→휠) 손실 → P_batt_chem에 직접 합산
  //   [A21-18-01] 동일 총합 보장:
  //     P_batt_chem = P_dc + gear_loss + batt_loss + aux = P_wheel + 전체손실 + aux ✓
  const P_ac = P_motor_mech + _motor_loss_eff;        // [Fix-②] 순수 AC 전력 (기어손실 제외)
  const P_dc = P_ac + _inv_loss_eff;                  // [Fix-②] 순수 DC 전력

  // [v5-fix1] Use computeBatteryLosses for consistent battery resistance model
  const I_batt = P_dc > 0 ? P_dc / params.V_dc : 0;
  const battLoss = computeBatteryLosses(I_batt, soc, T_batt, params);
  const P_loss_batt = battLoss.P_loss;
  const Ri = battLoss.Ri;
  // [Fix-Th2] hvacDetailedPower 반환값 객체 구조분해
  const _hvacV = hvacDetailedPower(params.T_amb, v, soc, params); // [A43-Fix-6] soc_init 고정→live soc
  params._hvac_prev_mode = _hvacV.new_hvac_mode;
  const P_aux = _hvacV.P;

  // Battery chemical power = DC power + gear loss (기계손실) + battery losses + aux
  const P_batt_chem = P_dc + gear.P_loss + P_loss_batt + P_aux; // [Fix-②] gear_loss 위치 이동

  // [Fix-CRIT-2] eta_gear 공식 교정
  //   수정 전: P_wheel/(P_wheel+gear.P_loss) — P_wheel=P_motor_mech이므로 분모가 과대 → η 과대 추정
  //   수정 후: P_wheel_actual/P_motor_mech  — 기어 출력/기어 입력 (정의에 맞는 전달 효율)
  const eta_gear      = P_motor_mech > 10 ? P_wheel_actual / P_motor_mech * 100 : 95;
  const eta_motor_val = P_motor_mech > 10 ? P_motor_mech / (P_motor_mech + _motor_loss_eff) * 100 : 90;
  const eta_inv       = P_ac > 10 ? P_ac / (P_ac + _inv_loss_eff) * 100 : 95;
  const eta_batt      = P_dc > 10 ? P_dc / (P_dc + P_loss_batt) * 100 : 98;

  // Debug: Log abnormal inverter efficiency
  if (eta_inv < 50 && P_ac > 5000) {  // [v6-fix7] 저부하 false-positive 방지
    console.warn('⚠️ Low Inverter Efficiency Detected:', {
      eta_inv: eta_inv.toFixed(1) + '%',
      P_ac: (P_ac/1000).toFixed(1) + 'kW',
      P_inv_loss: (_inv_loss_eff/1000).toFixed(1) + 'kW',
      I_rms: I_rms.toFixed(0) + 'A',
      V_dc: params.V_dc + 'V',
      motor_config: params.motor_config || 'default',
      breakdown: {
        P_cond: (_P_inv_cond_eff/1000).toFixed(2) + 'kW',
        P_sw:   (_P_inv_sw_eff/1000).toFixed(2)   + 'kW',
        P_aux:  (_P_inv_aux_eff/1000).toFixed(2)  + 'kW',
      }
    });
  }

  // eta_chain: 각 변환 단계 효율의 곱 (aux 왜곡 제외, 저부하 기본값)
  const eta_chain = (eta_batt / 100) * (eta_inv / 100) * (eta_motor_val / 100) * (eta_gear / 100) * 100;

  // [Fix-③] eta_total: 임의 가중치 0.6/0.4 제거 → 물리 기반 단일 경로 선택
  //   P_wheel_actual > 1kW (유효 부하): eta_direct = P_wheel_actual / P_drive_total (단일 측정 기반, 정확)
  //   P_wheel_actual ≤ 1kW (무부하/저부하): eta_direct 불안정 → eta_chain (각 단계 곱) 사용
  //   params.eta_total_method = 'chain' 강제 선택 가능 (캘리브레이션용)
  //   [Fix-CRIT-2] P_wheel → P_wheel_actual: 기어 손실 이후 실제 접지면 출력 기준
  const P_drive_total = P_dc + P_loss_batt; // aux 제외한 구동 전용 전력
  const eta_direct    = P_drive_total > 1000 ? P_wheel_actual / P_drive_total * 100 : 0;
  const eta_total     = (eta_direct > 5 && params.eta_total_method !== 'chain')
    ? +eta_direct.toFixed(1)          // 유효 부하 구간: 직접 측정값 우선
    : +eta_chain.toFixed(1);          // 저부하/강제 체인: 단계곱 사용

  const res = resistanceForcesV3(v, params, grade_rad, params.T_amb, T_cool, params.T_amb);

  return {
    // [Fix-CRIT-2] P_wheel: 실제 접지면 휠 출력 (P_motor_mech − gear.P_loss)
    //   P_motor_mech: 모터 샤프트 출력 (기어 손실 이전) — 별도 필드로 유지
    P_wheel: +(P_wheel_actual / 1000).toFixed(3), P_motor_mech: +(P_motor_mech / 1000).toFixed(3),
    P_ac: +(P_ac / 1000).toFixed(3), P_dc: +(P_dc / 1000).toFixed(3),
    P_batt_chem: +(P_batt_chem / 1000).toFixed(3), P_aux: +(P_aux / 1000).toFixed(3),
    P_loss_batt:  +(P_loss_batt / 1000).toFixed(4),
    P_loss_inv:   +(_inv_loss_eff   / 1000).toFixed(4),  // [A35-BUG-M2] 듀얼 스케일 적용값
    P_loss_motor: +(_motor_loss_eff / 1000).toFixed(4),  // [A35-BUG-M2] 듀얼 스케일 적용값
    P_loss_gear:  +(gear.P_loss / 1000).toFixed(4),
    P_cu:       +(_P_cu_eff       / 1000).toFixed(4),    // [A35-BUG-M2] 분배비 기반 동손
    P_fe:       +(_P_fe_eff       / 1000).toFixed(4),    // [A35-BUG-M2] 2× (듀얼시)
    // [Fix-WARN-4] P_pm_eddy 별도 노출: 자석 와전류 손실이 P_total에 합산은 되었으나
    //   에너지 체인 반환값에 없어 UI 손실 분석 차트에서 '기타'에 묻히던 문제 수정.
    //   듀얼 모터 시 2×, 싱글 시 1× 스케일 적용값 (_P_pm_eddy_eff).
    P_pm_eddy:  +(_P_pm_eddy_eff  / 1000).toFixed(4),    // [Fix-WARN-4] 자석 와전류 손실 (kW)
    P_mech_loss:+(_P_mech_eff     / 1000).toFixed(4),    // [A35-BUG-M2] 2× (듀얼시)
    P_aero: +(res.F_aero * v / 1000).toFixed(3), P_roll: +(res.F_roll * v / 1000).toFixed(3),
    P_grade: +(res.F_grade * v / 1000).toFixed(3),
    eta_batt: +eta_batt.toFixed(1), eta_inv: +eta_inv.toFixed(1),
    eta_motor: +eta_motor_val.toFixed(1), eta_gear: +eta_gear.toFixed(1), eta_total: +eta_total.toFixed(1),
    // Thermal heat generation [W] for thermalStep — 듀얼 모터 스케일값 반영
    Q_batt: P_loss_batt, Q_inv: _inv_loss_eff, Q_motor: _motor_loss_eff,
    // Temp state
    T_batt, T_inv, T_motor, T_cool,
    // Derate info
    derate_batt: +(P_dis_max_T(T_batt, params) / (params.P_dis_ref || params.P_max_battery || 250000) * 100).toFixed(0),
    derate_inv:   +(I_max_T(T_inv, params) / params.I_max_rms * 100).toFixed(0),
    derate_motor: +(T_max_motor_T(T_motor, params) / params.T_max_motor * 100).toFixed(0),
    Rs_eff: +motor.Rs_eff.toFixed(4), lambda_eff: +motor.lambda_eff.toFixed(4),
    // [Sprint-2] AWD 독립 모터 손실 진단 (싱글 시 null)
    awd_front_loss: _awd_front_loss ? {
      P_cu: +(_awd_front_loss.P_cu/1000).toFixed(4), P_fe: +(_awd_front_loss.P_fe/1000).toFixed(4),
      P_mech: +(_awd_front_loss.P_mech/1000).toFixed(4), P_total: +(_awd_front_loss.P_total/1000).toFixed(4),
    } : null,
    awd_rear_loss: _awd_rear_loss ? {
      P_cu: +(_awd_rear_loss.P_cu/1000).toFixed(4), P_fe: +(_awd_rear_loss.P_fe/1000).toFixed(4),
      P_mech: +(_awd_rear_loss.P_mech/1000).toFixed(4), P_total: +(_awd_rear_loss.P_total/1000).toFixed(4),
    } : null,
    Ri_eff: +Ri.toFixed(4), rho_eff: +(res.rho_eff || params.rho).toFixed(3),
    Crr_eff: +(res.Crr_eff || params.Crr).toFixed(5),
  };
}

// ── generateSteadyStateEfficiency ──
/**
 * 정상 상태 효율 맵 데이터를 생성한다 (10~300 km/h, 5 km/h 간격).
 * 각 속도점: computeEnergyChain으로 배터리·인버터·모터·기어 효율 산출.
 * [FIX-E4-HOT] thOverride: 온도 상태 재정의 (예: T_motor:140 과열 시나리오).
 * 기존 호출(인자 없음)은 thState=null → 하위 호환 유지.
 *
 * @param {object} params      차량 파라미터
 * @param {object} [thOverride]  { T_motor, T_inv, T_batt, T_cool } 온도 오버라이드
 * @returns {object[]}  { v_kmh, eta_batt, eta_inv, eta_motor, eta_gear, eta_total, kWh_100km, ... }[]
 */
function generateSteadyStateEfficiency(params, thOverride) {
  const data = [];
  const grade_rad = 0;
  for (let v_kmh = 10; v_kmh <= 300; v_kmh += 5) {
    const v = v_kmh / 3.6;
    const omega_m = v / params.r_wheel * params.gear_ratio;
    const res = resistanceForcesV3(v, params, grade_rad, params.T_amb, params.T_amb, params.T_amb);
    const T_ss = (res.F_total * params.r_wheel) / (params.gear_ratio * params.eta_driveline);
    if (T_ss <= 0 || T_ss > params.T_max_motor) continue;
    const p = params.poles / 2;
    const Iq = T_ss / (1.5 * p * params.lambda_pm);
    const chain = computeEnergyChain(v, omega_m, T_ss, 0, Iq, params.soc_init, params, grade_rad, thOverride || null);
    data.push({
      v_kmh, eta_batt: +chain.eta_batt, eta_inv: +chain.eta_inv,
      eta_motor: +chain.eta_motor, eta_gear: +chain.eta_gear, eta_total: +chain.eta_total,
      P_loss_batt: +chain.P_loss_batt, P_loss_inv: +chain.P_loss_inv,
      P_cu: +chain.P_cu, P_fe: +chain.P_fe, P_mech_loss: +chain.P_mech_loss,
      P_loss_gear: +chain.P_loss_gear, P_aux: +chain.P_aux,
      P_aero: +chain.P_aero, P_roll: +chain.P_roll,
      kWh_100km: +(chain.P_batt_chem / (v * 3.6) * 100).toFixed(1),
    });
  }
  return data;
}

// ── generateConditionComparison ──
/**
 * 온도·SOC 조건별 효율 비교 데이터를 생성한다.
 * 조건: -10°C / 0°C / 25°C / 40°C / 25°C SOC90% 5가지.
 * 속도 30·60·80·100·120·150 km/h 각 포인트에서 eta_total, kWh_100km, P_aux 산출.
 *
 * @param {object} params  차량 파라미터 (poles, lambda_pm, gear_ratio, r_wheel 등)
 * @returns {{ data: object[], conditions: string[] }}
 */
function generateConditionComparison(params) {
  const conditions = [
    { name: "-10°C", temp: -10, soc: 0.5 },
    { name: "0°C", temp: 0, soc: 0.5 },
    { name: "25°C", temp: 25, soc: 0.5 },
    { name: "40°C", temp: 40, soc: 0.5 },
    { name: "25°C SOC90%", temp: 25, soc: 0.9 },
  ];
  const speeds = [30, 60, 80, 100, 120, 150];
  const data = [];
  speeds.forEach((vk) => {
    const v = vk / 3.6;
    const omega_m = v / params.r_wheel * params.gear_ratio;
    const res = resistanceForcesV3(v, params, 0, 25, 25, 25);
    const T_ss = (res.F_total * params.r_wheel) / (params.gear_ratio * params.eta_driveline);
    if (T_ss <= 0 || T_ss > params.T_max_motor) return;
    const p = params.poles / 2;
    const Iq = T_ss / (1.5 * p * params.lambda_pm);
    const row = { v_kmh: vk };
    conditions.forEach((c) => {
      const p2 = { ...params, T_amb: c.temp, batt_temp: c.temp };
      const th = { T_batt: c.temp, T_inv: c.temp, T_motor: c.temp, T_cool: c.temp };
      const chain = computeEnergyChain(v, omega_m, T_ss, 0, Iq, c.soc, p2, 0, th);
      row[`eta_${c.name}`] = +chain.eta_total;
      row[`kWh_${c.name}`] = v > 1 ? +(chain.P_batt_chem / (v * 3.6) * 100).toFixed(1) : 0;
      row[`P_aux_${c.name}`] = +chain.P_aux;
    });
    data.push(row);
  });
  return { data, conditions: conditions.map((c) => c.name) };
}

// ── generateEnergyConsumption ──
/**
 * 속도별 에너지 소비량(kWh/100 km)과 항속 거리를 산출한다 (20~300 km/h, 5 km/h 간격).
 * 항속 거리: battery_energy × SOC 사용 범위 / (kWh/100 km) × 100. [B-2 Fix]
 *
 * @param {object} params  battery_energy, wltp_soc_usable_max/min, r_wheel, gear_ratio 등
 * @returns {object[]}  { v_kmh, kWh_100km, range_km, P_batt }[]
 */
function generateEnergyConsumption(params) {
  const data = [];
  for (let vk = 20; vk <= 300; vk += 5) {
    const v = vk / 3.6;
    const omega_m = v / params.r_wheel * params.gear_ratio;
    const res = resistanceForcesV3(v, params, 0, params.T_amb, params.T_amb, params.T_amb);
    const T_ss = (res.F_total * params.r_wheel) / (params.gear_ratio * params.eta_driveline);
    if (T_ss <= 0 || T_ss > params.T_max_motor) continue;
    const p = params.poles / 2;
    const Iq = T_ss / (1.5 * p * params.lambda_pm);
    const chain = computeEnergyChain(v, omega_m, T_ss, 0, Iq, params.soc_init, params, 0, null);
    const kWh = chain.P_batt_chem / (v * 3.6) * 100;
    data.push({
      v_kmh: vk, kWh_100km: +kWh.toFixed(1),
      range_km: kWh > 0 ? +(params.battery_energy * ((params.wltp_soc_usable_max || 0.90) - (params.wltp_soc_usable_min || 0.10)) / kWh * 100).toFixed(0) : 0, // [B-2 Fix]
      P_batt: +chain.P_batt_chem,
    });
  }
  return data;
}

// ══════════════════════════════════════════════════════════════════════
// ✅ RevC 미구현→구현 ③ — 물리 기반 노면 조건 Crr 모델
// ══════════════════════════════════════════════════════════════════════
//
// 기존 한계: road_condition → 단순 스케일 팩터 { dry:1.0, wet:1.15, snow:2.5, ice:1.8 }
//            물리 근거 없는 비율 → 속도·IRI·타이어 특성 반영 불가
//
// 개선 모델 (Schuring & Futamura 1990 + ISO 8608 기반):
//   Crr_total = Crr_base(v, T, p)
//             + ΔCrr_surface(road_type)
//             + ΔCrr_IRI(IRI, v)
//             + ΔCrr_water(h_water, v)   (wet 조건)
//             + ΔCrr_snow(h_snow)        (snow 조건)
//
// 노면 타입별 기저 Crr (Michelin TechCenter 공개 데이터):
//   dry asphalt   Crr ≈ 0.007–0.010
//   wet asphalt   Crr ≈ 0.009–0.013  (수막 점성 + 모세관 히스테리시스)
//   cobblestone   Crr ≈ 0.010–0.015  (국부 변형 증가)
//   gravel        Crr ≈ 0.015–0.025
//   packed snow   Crr ≈ 0.016–0.030
//   ice           Crr ≈ 0.010–0.018  (접지 감소이지만 변형도 감소)
//   dirt          Crr ≈ 0.025–0.040
//
// IRI(International Roughness Index) → ΔCrr (Múčka 2017):
//   ΔCrr_IRI = k_IRI·IRI + k_IRI_v·IRI·v  (v: m/s)
//
// 반환: { Crr_total, Crr_base, dCrr_surface, dCrr_IRI, dCrr_water, dCrr_snow,
//          road_type, IRI, surface_quality }
// ══════════════════════════════════════════════════════════════════════

/** 노면 타입별 기저 Crr 테이블 (속도=0 기준, 25°C, 정격 공기압) */
const ROAD_SURFACE_CRR = {
  // [road_type]: { Crr_ref, k_v, label_ko }
  // k_v: 속도 계수 [s²/m²] — Crr_v = Crr_ref·(1 + k_v·v²)
  dry_smooth:    { Crr_ref: 0.0065, k_v: 4.5e-5, label_ko: '건조 평활 아스팔트' },
  dry:           { Crr_ref: 0.0080, k_v: 5.0e-5, label_ko: '건조 아스팔트' },
  dry_rough:     { Crr_ref: 0.0100, k_v: 6.0e-5, label_ko: '건조 거친 아스팔트' },
  wet:           { Crr_ref: 0.0110, k_v: 7.0e-5, label_ko: '습윤 아스팔트' },
  wet_standing:  { Crr_ref: 0.0140, k_v: 9.5e-5, label_ko: '고인물 습윤' },
  cobblestone:   { Crr_ref: 0.0130, k_v: 8.0e-5, label_ko: '돌블록(코블스톤)' },
  gravel:        { Crr_ref: 0.0200, k_v: 7.5e-5, label_ko: '자갈길' },
  packed_snow:   { Crr_ref: 0.0250, k_v: 5.5e-5, label_ko: '다진 눈길' },
  loose_snow:    { Crr_ref: 0.0350, k_v: 5.0e-5, label_ko: '눈길(미끄럼)' },
  ice:           { Crr_ref: 0.0130, k_v: 4.0e-5, label_ko: '빙판' },
  dirt:          { Crr_ref: 0.0300, k_v: 8.5e-5, label_ko: '비포장 흙길' },
  // 하위 호환 — road_condition 키 그대로 수용
  snow:          { Crr_ref: 0.0280, k_v: 5.5e-5, label_ko: '눈길' },
  dry_asphalt:   { Crr_ref: 0.0080, k_v: 5.0e-5, label_ko: '건조 아스팔트' },
};

/**
 * 물리 기반 노면 Crr 통합 계산
 *
 * @param {number} v         — 차속 [m/s]
 * @param {number} T_tire    — 타이어 온도 [°C]
 * @param {object} params
 *   params.road_type / params.road_condition — 노면 타입 키
 *   params.Crr               — 기본 Crr (road_type 미지정 시 fallback)
 *   params.Crr_v_coeff       — 기존 속도 계수 (fallback)
 *   params.Crr_temp_coeff    — 기존 온도 계수 (fallback)
 *   params.tire_pressure_nominal/actual — 공기압 [bar]
 *   params.IRI               — IRI [m/km] (기본 2.5)
 *   params.k_IRI_crr         — IRI→Crr 선형 계수
 *   params.k_IRI_vib         — IRI→Crr 속도 의존 계수
 *   params.h_water_mm        — 수막 두께 [mm] (wet 조건, 기본 1.5)
 *   params.h_snow_mm         — 눈 깊이 [mm] (snow 조건, 기본 0)
 */
function roadSurfaceCrrModel(v, T_tire, params) {
  // ── 노면 타입 선택 ────────────────────────────────────────────
  const road_key = params.road_type || params.road_condition || 'dry';
  const surface  = ROAD_SURFACE_CRR[road_key] || ROAD_SURFACE_CRR['dry'];

  // ── 기저 Crr: 속도 의존 ───────────────────────────────────────
  const Crr_ref  = surface.Crr_ref;
  const k_v_surf = surface.k_v;
  const Crr_base_v = Crr_ref * (1 + k_v_surf * v * v);

  // ── 온도 보정 (Michelin 모델: <20°C → 점탄성 히스테리시스 증가) ─
  let f_T = 1.0;
  if (T_tire < 20) {
    // 저온: 고무 경화 → 구름저항 증가 (10°C당 약 +8%)
    f_T = 1 + 0.008 * (20 - T_tire);
  } else if (T_tire > 80) {
    // 고온: 과도 연화 → 약간 증가
    f_T = 1 + 0.002 * (T_tire - 80);
  }
  f_T = Math.max(0.85, Math.min(1.40, f_T));
  const Crr_base = Crr_base_v * f_T;

  // ── 공기압 보정 ──────────────────────────────────────────────
  const p_nom   = params.tire_pressure_nominal || 2.4;  // [bar]
  const p_act   = params.tire_pressure_actual  || 2.4;
  const dp      = (p_nom - p_act) / p_nom;              // 무차원 압력 편차
  const alpha_p = params.tire_pressure_crr_alpha || 0.008;
  const beta_p  = params.tire_pressure_crr_beta  || 0.0003;
  const dCrr_p  = Math.max(0, alpha_p * dp + beta_p * dp * dp);

  // ── IRI 노면 거칠기 추가 저항 (Múčka 2017 모델) ─────────────
  const IRI        = params.IRI || 2.5;       // [m/km]
  const k_IRI_crr  = params.k_IRI_crr  || 0.00015;
  const k_IRI_vib  = params.k_IRI_vib  || 0.000008;
  // 노면 타입별 IRI 민감도 조정
  //   거친 노면은 IRI 효과가 중복 → 감쇠; 부드러운 노면은 IRI가 지배적
  const f_IRI_surf = road_key.startsWith('dry') ? 1.0
                   : road_key === 'cobblestone' ? 0.6
                   : road_key.includes('snow')  ? 0.5
                   : road_key === 'gravel'       ? 0.4
                   : 0.8;
  const dCrr_IRI   = (k_IRI_crr * IRI + k_IRI_vib * IRI * v) * f_IRI_surf;

  // ── 수막 추가 저항 (Grogger & Weiss 1996) ────────────────────
  //   ΔCrr_water = k_w · h_water · v²  (수막 점성 전단 + 배수 에너지)
  let dCrr_water = 0;
  if (params.h_water_mm > 0 || road_key === 'wet' || road_key === 'wet_standing') {
    const h_mm  = params.h_water_mm || (road_key === 'wet_standing' ? 3.0 : 1.5);
    const k_w   = params.k_water_crr || 1.2e-6;  // [s²/(m²·mm)]
    dCrr_water  = Math.max(0, k_w * h_mm * v * v);
  }

  // ── 눈길 압착 저항 (Wong 2001) ───────────────────────────────
  //   ΔCrr_snow = k_sn · h_snow / (p_tire_kPa)^0.5
  let dCrr_snow = 0;
  if (params.h_snow_mm > 0 || road_key === 'packed_snow' || road_key === 'loose_snow' || road_key === 'snow') {
    const h_sn     = params.h_snow_mm || 30;     // [mm]
    const p_kPa    = (p_act || 2.4) * 100;       // bar → kPa
    const k_sn     = params.k_snow_crr || 0.0003; // [1/(mm·kPa^0.5)]
    dCrr_snow      = Math.max(0, k_sn * h_sn / Math.sqrt(p_kPa));
  }

  // ── 합산 ────────────────────────────────────────────────────
  const Crr_total = Crr_base + dCrr_p + dCrr_IRI + dCrr_water + dCrr_snow;

  // 노면 품질 등급 (IRI 기준, 국제 도로 연맹 표준)
  const surface_quality = IRI < 2   ? 'A-very_good'
                        : IRI < 4   ? 'B-good'
                        : IRI < 7   ? 'C-fair'
                        : IRI < 12  ? 'D-poor'
                        : 'E-very_poor';

  return {
    Crr_total: +Crr_total.toFixed(6),
    Crr_base:  +Crr_base.toFixed(6),
    dCrr_surface: +(Crr_base - Crr_ref * f_T).toFixed(6),
    dCrr_pressure: +dCrr_p.toFixed(6),
    dCrr_IRI:  +dCrr_IRI.toFixed(6),
    dCrr_water: +dCrr_water.toFixed(6),
    dCrr_snow:  +dCrr_snow.toFixed(6),
    road_type: road_key,
    label_ko:  surface.label_ko,
    IRI,
    surface_quality,
    f_T: +f_T.toFixed(3),
  };
}

export {
  rhoCorrected,
  CrrDynamic,
  CrrWithPressure,
  resistanceForcesV3,
  rlResist,
  rlSteadyPower,
  rlPmaxAtV,
  rlCalcVcross,
  rlCalcVopt,
  rlCalcVmax,
  rlCalcVmaxTorque,
  rlBuildR1,
  rlBuildR2base,
  rlBuildR3,
  rlBuildR4,
  simRoadLoad,
  initVehiclePhysicsState,
  stepVehiclePhysics,
  variableCdA,
  gearLossTempEnhanced,
  initGearState,
  getActiveGear,
  computeEnergyChain,
  generateSteadyStateEfficiency,
  generateConditionComparison,
  generateEnergyConsumption,
  // ✅ RevC
  roadSurfaceCrrModel,      // 물리 기반 노면 Crr (IRI + 수막 + 눈길 + 압력)
  ROAD_SURFACE_CRR,         // 노면 타입별 기저 Crr 테이블
};